// Lesiones de piel, segunda versión del registro (2026-10-10).
//
// Por qué existe: el registro de piel nació para las lesiones por presión del
// piloto (12 puntos de apoyo, seis tipos, una descripción corta). El fundador
// pidió un cuerpo ficticio donde se pueda poner cualquier lesión y describirla
// sin sacar fotos, y una revisión externa marcó lo que había que corregir:
// el programa no puede inventar hallazgos, lo no evaluado tiene que seguir
// diciendo que no se evaluó, la categoría pendiente no es "no clasificable",
// una lesión detectada en el turno no es una lesión adquirida y la evolución
// solo puede decir lo que alguien registró.
//
// Este módulo es puro y no importa nada de la app (pielLpp.js lo importa a él,
// no al revés, para no crear ciclos). Guarda tres cosas:
//   1. los catálogos cerrados (ubicación, tipos, clasificaciones, hallazgos);
//   2. las funciones que construyen y actualizan una lesión, con su historia
//      (valoraciones sucesivas, reclasificaciones, correcciones, curaciones);
//   3. la frase clínica, que sale SOLO de lo registrado.
//
// Doctrina que no se negocia:
//   - Un campo sin respuesta es `undefined`. "No evaluado", "no visible", "no
//     medido", "no aplica" y "ausente" son respuestas distintas y se guardan
//     como tales (ESTADO). Ninguna se convierte en un hallazgo normal.
//   - Nada se normaliza en silencio: un porcentaje que no suma 100 se reporta
//     como problema (validarTejido) y la UI ayuda a corregirlo; una medida que
//     no es número se rechaza, no se redondea ni se completa.
//   - Una valoración nueva empieza vacía. La anterior se muestra como
//     referencia, jamás se copia como si se hubiera vuelto a mirar.
//   - Los datos de una lesión son datos clínicos de una persona: aunque sean
//     de catálogo cerrado siguen siendo sensibles si se pueden vincular a ella.
//     Por eso no viajan a la telemetría (ver PielPanel) ni a servicios
//     externos, y viven en el registro efímero del turno como el resto.

export const VERSION_LESION = 2;

// ── Respuestas que no son un hallazgo ─────────────────────────────────────────
// Cada una dice algo distinto y la frase las respeta:
//   no_evaluado → se pudo mirar y no se miró (o no se registró el resultado);
//   no_visible  → no se puede ver (lecho cubierto, zona bajo un dispositivo fijo);
//   no_medido   → no se midió;
//   no_aplica   → la pregunta no corresponde a esta lesión;
//   ausente     → se miró y no está.
export const ESTADO = {
  NO_EVALUADO: 'no_evaluado',
  NO_VISIBLE: 'no_visible',
  NO_MEDIDO: 'no_medido',
  NO_APLICA: 'no_aplica',
  AUSENTE: 'ausente',
};
export const ESTADOS = Object.values(ESTADO);
export const esEstado = (v) => ESTADOS.includes(v);

// ── Ubicación ─────────────────────────────────────────────────────────────────
// Regiones en orden céfalo-caudal. Cada detalle puede:
//   - `zona`: corresponder a un punto de apoyo del catálogo histórico
//     (ZONAS_PRESION de pielLpp), así el anclaje de la curación dictada, el
//     segmento del examen y la localización de la evolución siguen funcionando;
//   - `central`: no tener lado (sacro, lengua);
//   - `tejido: 'mucosa'`: ser mucosa. Nariz y labios tienen piel Y mucosa, y la
//     LPP de mucosa no se categoriza, así que el detalle lo deja explícito.
// `segmento` ata la región a la grilla de 9 segmentos del examen físico.
export const REGIONES = [
  { id: 'cabeza', label: 'Cabeza', segmento: 'cabeza', detalles: [
    { id: 'occipucio', label: 'Occipucio', zona: 'occipucio', central: true },
    { id: 'cuero_cabelludo', label: 'Cuero cabelludo' },
  ] },
  { id: 'cara', label: 'Cara', segmento: 'cabeza', detalles: [
    { id: 'frente', label: 'Frente', central: true },
    { id: 'puente_nasal', label: 'Puente nasal', zona: 'nariz', central: true },
    { id: 'ala_nasal', label: 'Ala nasal', zona: 'nariz' },
    { id: 'fosa_nasal', label: 'Mucosa nasal', zona: 'nariz', tejido: 'mucosa' },
    { id: 'mejilla', label: 'Mejilla' },
    { id: 'comisura_labial', label: 'Comisura labial', zona: 'boca' },
    { id: 'labio', label: 'Labio, piel', zona: 'boca', central: true },
    { id: 'mucosa_labial', label: 'Mucosa del labio', zona: 'boca', tejido: 'mucosa', central: true },
    { id: 'lengua', label: 'Lengua', zona: 'boca', tejido: 'mucosa', central: true },
    { id: 'menton', label: 'Mentón', central: true },
    { id: 'oreja', label: 'Pabellón auricular', zona: 'oreja' },
    { id: 'retroauricular', label: 'Detrás de la oreja', zona: 'oreja' },
  ] },
  { id: 'cuello', label: 'Cuello', segmento: 'cuello', detalles: [
    { id: 'estoma_tqt', label: 'Alrededor del estoma de traqueostomía', central: true },
    { id: 'cuello_anterior', label: 'Cara anterior del cuello', central: true },
    { id: 'cuello_lateral', label: 'Cara lateral del cuello' },
    { id: 'nuca', label: 'Nuca', central: true },
  ] },
  { id: 'torax', label: 'Tórax', segmento: 'torax', detalles: [
    { id: 'subclavicular', label: 'Zona subclavicular' },
    { id: 'esternal', label: 'Zona esternal', central: true },
    { id: 'pliegue_inframamario', label: 'Pliegue inframamario' },
    { id: 'axila', label: 'Axila' },
  ] },
  { id: 'espalda', label: 'Espalda', segmento: 'espalda', detalles: [
    { id: 'escapula', label: 'Escápula', zona: 'escapula' },
    { id: 'columna_dorsal', label: 'Columna dorsal', central: true },
    { id: 'zona_lumbar', label: 'Zona lumbar' },
  ] },
  { id: 'abdomen', label: 'Abdomen', segmento: 'abdomen', detalles: [
    { id: 'periumbilical', label: 'Zona periumbilical', central: true },
    { id: 'flanco', label: 'Flanco' },
    { id: 'hipogastrio', label: 'Hipogastrio', central: true },
    { id: 'periestomal', label: 'Alrededor de una ostomía' },
    { id: 'pliegue_abdominal', label: 'Pliegue bajo el panículo abdominal' },
  ] },
  { id: 'pelvis', label: 'Sacro y glúteos', segmento: 'sacra', detalles: [
    { id: 'sacro', label: 'Sacro', zona: 'sacro', central: true },
    { id: 'coxis', label: 'Coxis', central: true },
    { id: 'gluteo', label: 'Glúteo' },
    { id: 'isquion', label: 'Isquion', zona: 'isquion' },
    { id: 'pliegue_intergluteo', label: 'Pliegue interglúteo', central: true },
  ] },
  { id: 'perine', label: 'Periné y genitales', segmento: 'genitales', detalles: [
    { id: 'perianal', label: 'Zona perianal', central: true },
    { id: 'genitales', label: 'Genitales', central: true },
    { id: 'pliegue_inguinal', label: 'Pliegue inguinal' },
    { id: 'meato', label: 'Meato uretral', tejido: 'mucosa', central: true },
    { id: 'muslo_interno', label: 'Cara interna del muslo' },
  ] },
  { id: 'cadera', label: 'Cadera', segmento: 'sacra', detalles: [
    { id: 'trocanter', label: 'Trocánter', zona: 'trocanter' },
    { id: 'cresta_iliaca', label: 'Cresta ilíaca' },
  ] },
  { id: 'miembro_superior', label: 'Brazo y mano', segmento: 'eess', detalles: [
    { id: 'hombro', label: 'Hombro' },
    { id: 'brazo', label: 'Brazo' },
    { id: 'codo', label: 'Codo', zona: 'codo' },
    { id: 'antebrazo', label: 'Antebrazo' },
    { id: 'muneca', label: 'Muñeca' },
    { id: 'dorso_mano', label: 'Dorso de la mano' },
    { id: 'palma', label: 'Palma de la mano' },
    { id: 'dedos_mano', label: 'Dedos de la mano' },
  ] },
  { id: 'miembro_inferior', label: 'Pierna y pie', segmento: 'eeii', detalles: [
    { id: 'muslo', label: 'Muslo' },
    { id: 'rodilla', label: 'Rodilla', zona: 'rodilla' },
    { id: 'hueco_popliteo', label: 'Hueco poplíteo' },
    { id: 'pierna', label: 'Pierna' },
    { id: 'maleolo_interno', label: 'Maléolo interno', zona: 'maleolo' },
    { id: 'maleolo_externo', label: 'Maléolo externo', zona: 'maleolo' },
    { id: 'dorso_pie', label: 'Dorso del pie' },
    { id: 'dedos_pie', label: 'Dedos del pie' },
    { id: 'talon', label: 'Talón', zona: 'talon' },
    { id: 'planta', label: 'Planta del pie' },
  ] },
];
export const LADOS = ['der', 'izq'];
const LADO_TXT = { der: 'derecho', izq: 'izquierdo' };
// Los labels femeninos concuerdan con el lado ("escápula izquierda").
const FEMENINOS = new Set(['escapula', 'oreja', 'retroauricular', 'mejilla', 'ala_nasal', 'fosa_nasal', 'comisura_labial', 'axila', 'zona_lumbar', 'zona_subclavicular', 'subclavicular', 'cresta_iliaca', 'muneca', 'mano', 'dorso_mano', 'palma', 'pierna', 'rodilla', 'planta', 'cara', 'cuello_lateral', 'zona_perianal']);

export function regionDef(id) { return REGIONES.find((r) => r.id === id) || null; }
export function detalleDef(regionId, detalleId) {
  return regionDef(regionId)?.detalles.find((d) => d.id === detalleId) || null;
}
// Busca un detalle por su id en todas las regiones (los ids son únicos).
export function detallePorId(detalleId) {
  for (const r of REGIONES) {
    const d = r.detalles.find((x) => x.id === detalleId);
    if (d) return { region: r, detalle: d };
  }
  return null;
}

// Normaliza una ubicación a catálogo cerrado. Lo que no está en el catálogo se
// descarta; jamás aterriza texto libre. El punto del cuerpo son dos números.
export function normalizarUbicacion(u) {
  if (!u || typeof u !== 'object') return null;
  const region = regionDef(u.region);
  if (!region) return null;
  const detalle = region.detalles.find((d) => d.id === u.detalle) || null;
  const central = Boolean(detalle?.central);
  const lado = !central && LADOS.includes(u.lado) ? u.lado : null;
  const tejido = detalle?.tejido === 'mucosa' ? 'mucosa' : 'piel';
  const out = { region: region.id, detalle: detalle?.id || null, lado, tejido };
  if (u.vista === 'anterior' || u.vista === 'posterior') out.vista = u.vista;
  const x = Number(u.punto?.x), y = Number(u.punto?.y);
  if (Number.isFinite(x) && Number.isFinite(y) && x >= 0 && x <= 200 && y >= 0 && y <= 440) {
    out.punto = { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
  }
  if (u.bajoDispositivo === true) out.bajoDispositivo = true;
  if (u.confirmada === true) out.confirmada = true;
  return out;
}

// "sacro", "talón izquierdo", "mucosa del labio", "antebrazo derecho".
export function ubicacionTexto(u) {
  const n = normalizarUbicacion(u);
  if (!n) return 'ubicación sin registrar';
  const region = regionDef(n.region);
  const det = n.detalle ? region.detalles.find((d) => d.id === n.detalle) : null;
  const base = (det?.label || region.label).toLowerCase();
  if (!n.lado) return base;
  const fem = FEMENINOS.has(n.detalle || n.region);
  return `${base} ${fem ? LADO_TXT[n.lado].replace(/o$/, 'a') : LADO_TXT[n.lado]}`;
}

// El punto de apoyo histórico (ZONAS_PRESION) que corresponde a la ubicación,
// o 'otra'. Así las funciones que ya existían (anclarCuracion, el mapa del
// examen, la localización de la evolución) siguen leyendo `lesion.zona`.
export function zonaDeUbicacion(u) {
  const n = normalizarUbicacion(u);
  if (!n) return 'otra';
  const det = n.detalle ? detalleDef(n.region, n.detalle) : null;
  return det?.zona || 'otra';
}
export function segmentoDeUbicacion(u) {
  const n = normalizarUbicacion(u);
  return n ? regionDef(n.region).segmento : null;
}

// ── Tipos de lesión ───────────────────────────────────────────────────────────
// `id` es lo que se guarda. LPP y DAI conservan el string histórico porque la
// evolución, el SBAR y el mapa ya los reconocen. `grupo` ordena el selector.
export const TIPOS = [
  { id: 'LPP', label: 'Lesión por presión', corto: 'LPP', grupo: 'presion' },
  { id: 'DAI', label: 'Dermatitis asociada a incontinencia', corto: 'DAI', grupo: 'humedad' },
  { id: 'humedad', label: 'Otra lesión por humedad', corto: 'Pliegues, ostomía o alrededor de una herida', grupo: 'humedad' },
  { id: 'desgarro', label: 'Desgarro cutáneo', corto: 'Skin tear', grupo: 'mecanica' },
  { id: 'MARSI', label: 'Lesión por adhesivo médico', corto: 'MARSI', grupo: 'mecanica' },
  { id: 'operatoria', label: 'Herida operatoria', grupo: 'heridas' },
  { id: 'quemadura', label: 'Quemadura', grupo: 'heridas' },
  { id: 'ulcera_venosa', label: 'Úlcera venosa', grupo: 'cronicas' },
  { id: 'ulcera_arterial', label: 'Úlcera arterial', grupo: 'cronicas' },
  { id: 'pie_diabetico', label: 'Úlcera de pie diabético', grupo: 'cronicas' },
  { id: 'equimosis', label: 'Equimosis', grupo: 'sangre' },
  { id: 'hematoma', label: 'Hematoma', grupo: 'sangre' },
  { id: 'por_definir', label: 'Lesión por definir', corto: 'Aún no sabes qué es', grupo: 'otra' },
];
export const GRUPOS_TIPO = [
  ['presion', 'Por presión'], ['humedad', 'Por humedad'], ['mecanica', 'Por roce o adhesivos'],
  ['heridas', 'Heridas'], ['cronicas', 'Úlceras de pierna y pie'], ['sangre', 'Sangre bajo la piel'], ['otra', 'Si aún no sabes'],
];
// Tipos del registro anterior: se LEEN tal como quedaron, no se ofrecen ni se
// reinterpretan. Un "eritema que no blanquea" viejo no pasa a ser LPP I.
export const TIPOS_LEGADO = ['Eritema que no blanquea', 'Herida', 'Maceración', 'Otra'];
export const tipoDef = (id) => TIPOS.find((t) => t.id === id) || null;
// Concordancia de la frase: "hematoma detectado", "LPP detectada". Solo dos
// nombres son masculinos.
const MASCULINOS = new Set(['desgarro', 'hematoma']);
export const esMasculino = (l) => MASCULINOS.has(l?.tipo);
export const concuerda = (l, femenino) => (esMasculino(l) ? femenino.replace(/a$/, 'o') : femenino);

// ── Clasificación por tipo ────────────────────────────────────────────────────
// `pendiente` es una respuesta propia: "todavía no se sabe". No es "no
// clasificable" (que en LPP es una categoría con criterio) ni un valor vacío.
export const PENDIENTE = 'pendiente';

// Nombres de la Orientación Técnica MINSAL de prevención de LPP (2023, tabla 1),
// que traduce el sistema NPIAP: Categoría I a IV, LPP no clasificable y LPP en
// tejidos profundos.
export const CATEGORIAS_LPP_V2 = ['Categoría I', 'Categoría II', 'Categoría III', 'Categoría IV', 'No clasificable', 'Tejidos profundos'];
// El orden numérico solo existe entre I y IV: no clasificable y tejidos
// profundos no son "más" ni "menos" que una categoría numerada.
export const RANGO_CATEGORIA = { 'Categoría I': 1, 'Categoría II': 2, 'Categoría III': 3, 'Categoría IV': 4 };
// Nombres del registro anterior que se siguen leyendo.
export const CATEGORIA_LEGADO = { DTPI: 'Tejidos profundos', 'No estadiable': 'No clasificable' };

// Una sola clasificación por tipo. La DAI usa GLOBIAD (1A/1B sin o con signos
// de infección sobre enrojecimiento persistente, 2A/2B sobre pérdida de piel):
// la OT MINSAL 2025 usa las mismas letras con otro significado (GNEAUPP, por
// intensidad), por eso la app dice cuál usa. La quemadura usa Benaim, la
// referencia chilena (Guía GES Gran Quemado 2016). La úlcera venosa usa la
// clase clínica CEAP de la pierna (C6 activa, C6r activa recurrente), que
// describe la pierna y no gradúa la úlcera. El pie diabético no tiene una
// clasificación única: se registran las seis variables de SINBAD por separado
// (IWGDF 2023) y Wagner no se ofrece porque la IWGDF no lo recomienda y la
// orientación MINSAL de 2018 tampoco.
export const CLASIFICACION = {
  LPP: { campo: 'categoria', label: 'Categoría', opciones: CATEGORIAS_LPP_V2 },
  DAI: { campo: 'categoria', label: 'Categoría GLOBIAD', opciones: ['Categoría 1A', 'Categoría 1B', 'Categoría 2A', 'Categoría 2B'] },
  // Subtipos de LESCAH de la OT MINSAL 2025 (pág. 8), sin la DAI, que tiene
  // tipo propio. "Perilesional" es la que rodea una herida (hasta 4 cm).
  humedad: { campo: 'subtipo', label: 'Cuál', opciones: ['Intertriginosa', 'Perilesional', 'Periestomal', 'Por saliva o mucosidad', 'Asociada a exudado de la piel'] },
  desgarro: { campo: 'categoria', label: 'Tipo ISTAP', opciones: ['Tipo 1', 'Tipo 2', 'Tipo 3'] },
  MARSI: { campo: 'subtipo', label: 'Qué se observa', opciones: ['Desprendimiento de la epidermis', 'Lesión por tensión o flictena', 'Dermatitis de contacto', 'Maceración', 'Foliculitis'] },
  operatoria: { campo: 'estadoHerida', label: 'Estado de los bordes', opciones: ['Afrontada', 'Dehiscencia parcial', 'Dehiscencia total'] },
  quemadura: { campo: 'categoria', label: 'Profundidad (Benaim)', opciones: ['Tipo A', 'Tipo AB-A', 'Tipo AB-B', 'Tipo B'] },
  ulcera_venosa: { campo: 'categoria', label: 'Clase CEAP de la pierna', opciones: ['C6, úlcera activa', 'C6r, úlcera activa recurrente'] },
};

// ── Hallazgos de la valoración ────────────────────────────────────────────────
// Las listas son catálogo cerrado; las fuentes de cada una están en
// docs/plan-piel-sin-fotos.md (sección de fuentes).
export const TEJIDOS_LECHO = [
  { k: 'granulacion', label: 'Granulación' },
  { k: 'esfacelo', label: 'Esfacelo' },
  { k: 'necrosis', label: 'Necrosis o escara' },
  { k: 'epitelizacion', label: 'Epitelización' },
  { k: 'otro', label: 'Otro tejido' },
];
export const EXUDADO_CANTIDAD = ['Sin exudado', 'Escaso', 'Moderado', 'Abundante'];
export const EXUDADO_TIPO = ['Seroso', 'Serohemático', 'Hemático', 'Turbio', 'Purulento'];
export const BORDES = ['Definidos', 'Irregulares', 'Enrollados (epíbole)', 'Macerados', 'Hiperqueratósicos', 'En avance, epitelizando'];
export const PERILESIONAL = ['Sana', 'Eritema o cambio de tono', 'Maceración', 'Induración', 'Edema', 'Descamación o sequedad', 'Excoriación', 'Calor'];
export const ESTRUCTURAS = ['Hueso', 'Tendón', 'Músculo', 'Fascia', 'Cartílago', 'Ligamento'];

// Signos de infección como HALLAZGOS, con las palabras del continuo de
// infección del IWII (4.ª ed., 2026, págs. 21 a 23). Encubierta y evidente son
// dos presentaciones de la infección LOCAL. El grupo enseña; no diagnostica:
// la frase lista lo observado y la interpretación existe solo si la persona la
// elige. El IWII pide sospechar ante varios signos, no por uno solo, y el corte
// de 2 cm describe dónde llega el eritema, no es una prueba por sí solo.
export const SIGNOS_INFECCION = [
  { grupo: 'Infección local encubierta (sutil)', signos: ['Retraso de la cicatrización', 'Hipergranulación', 'Granulación friable que sangra', 'Exudado en aumento', 'Puentes de epitelio o bolsillos en la granulación'] },
  { grupo: 'Infección local evidente', signos: ['Eritema o cambio de tono a menos de 2 cm del borde', 'Calor local', 'Aumento de volumen', 'Secreción purulenta', 'Dehiscencia o aumento de tamaño', 'Dolor nuevo o en aumento', 'Mal olor en aumento'] },
  { grupo: 'Infección que se extiende', signos: ['Induración que se extiende', 'Eritema o inflamación a más de 2 cm del borde', 'Crepitación', 'Dehiscencia con o sin lesiones satélite', 'Linfangitis'] },
  { grupo: 'Manifestaciones generales', signos: ['Malestar general', 'Letargo o deterioro general', 'Pérdida de apetito', 'Fiebre'] },
];
export const SIGNOS_INFECCION_TODOS = SIGNOS_INFECCION.flatMap((g) => g.signos);
export const SIGNOS_GENERALES = SIGNOS_INFECCION[3].signos;
// La interpretación es de la persona, elegida a mano. Jamás se deriva sola.
export const INTERPRETACION_INFECCION = ['Sin sospecha de infección', 'Sospecha de infección local', 'Sospecha de infección que se extiende', 'Sospecha de infección con compromiso general'];

export const DOLOR_MOMENTO = ['En reposo', 'Durante la curación'];
export const TRAYECTORIA = ['Mejoría', 'Estacionaria', 'Empeoramiento'];
// Cómo lo dice la frase: la trayectoria es un juicio de quien valora y va al
// final, sin rótulo con dos puntos.
export const TRAYECTORIA_PROSA = { 'Mejoría': 'en mejoría', 'Estacionaria': 'estacionaria', 'Empeoramiento': 'empeorando' };

export const DISPOSITIVOS_LPP = ['SNG o sonda nasoyeyunal', 'Tubo orotraqueal', 'Traqueostomía', 'Mascarilla de VNI', 'Naricera o CNAF', 'Sonda urinaria', 'Collar cervical', 'Saturómetro', 'Férula o yeso', 'Catéter o línea', 'Sujeción', 'Otro dispositivo'];
export const PRODUCTO_ADHESIVO = ['Apósito adhesivo', 'Cinta o tela adhesiva', 'Fijación de un dispositivo', 'Electrodo', 'Apósito de catéter venoso', 'Bolsa de ostomía', 'Otro producto'];
export const MECANISMO_DESGARRO = ['Roce o fricción', 'Tracción o cizalla', 'Golpe o caída', 'Retiro de un adhesivo', 'Desconocido'];
export const CERTEZA_DERMATITIS = ['Sin determinar si es irritativa o alérgica', 'Irritativa, diagnóstico establecido', 'Alérgica, diagnóstico establecido'];
export const PERSISTE_30 = ['Persiste 30 minutos o más tras retirar el adhesivo', 'Cede antes de 30 minutos', 'No evaluado'];
export const CIERRE_OPERATORIA = ['Corchetes', 'Sutura', 'Adhesivo tisular', 'Cierre por segunda intención'];
// Equimosis y hematoma: contexto referido u observado, sin asignar causa. El
// anticoagulante no es un contexto de la lesión sino un dato del paciente que
// pesa al valorarla, por eso va aparte (campo `anticoagulacion`).
export const CONTEXTO_SANGRE = ['Golpe o caída', 'Punción venosa', 'Punción o acceso arterial', 'Inyección subcutánea', 'Sitio quirúrgico o de procedimiento', 'Sin causa conocida'];
export const FUENTE_CONTEXTO = ['Referido', 'Observado'];

// Campos simples de la valoración: un valor de catálogo o una lista. `aplica`
// decide si se pregunta; `prosa` arma el fragmento, solo con lo registrado.
// El orden del arreglo ES el orden en la frase.
// Baja la primera letra para insertar un rótulo en la frase, salvo en una
// sigla ("SNG o sonda…" no puede quedar "sNG").
const m = (s) => { const t = String(s); return /^[A-ZÁÉÍÓÚÑ]{2}/.test(t) ? t : t.charAt(0).toLocaleLowerCase() + t.slice(1); };
const enLista = (xs) => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} y ${xs[xs.length - 1]}`);
export const CAMPOS_SIMPLES = [
  { k: 'presentacionTP', label: 'Cómo se presenta', tipo: 'multi', nivel: 'esencial',
    opciones: ['Piel intacta morada o granate', 'Ampolla con contenido hemático', 'Separación de la epidermis con lecho oscuro'],
    aplica: (l) => l.tipo === 'LPP' && l.categoria === 'Tejidos profundos', prosa: (v) => m(enLista(v)) },
  { k: 'ampolla', label: '¿Hay ampolla?', tipo: 'select', nivel: 'esencial',
    opciones: ['Ampolla serosa intacta', 'Ampolla serosa rota', 'Sin ampolla, lecho rosado o rojo'],
    aplica: (l) => l.tipo === 'LPP' && l.categoria === 'Categoría II', prosa: (v) => m(v) },
  { k: 'escara', label: 'La escara es', tipo: 'select', nivel: 'detalle',
    opciones: ['Estable: seca, adherida, sin eritema ni fluctuación', 'Inestable: húmeda, desprendida, con eritema o fluctuación'],
    aplica: (l) => l.tipo === 'LPP' && l.categoria === 'No clasificable', prosa: (v) => `escara ${m(v.split(':')[0])}` },
  { k: 'colgajoViabilidad', label: 'Colgajo', tipo: 'select', nivel: 'esencial',
    opciones: ['Viable', 'De viabilidad dudosa', 'No viable'],
    aplica: (l) => l.tipo === 'desgarro' && l.categoria !== 'Tipo 3', prosa: (v) => `colgajo ${m(v)}` },
  // El tipo ISTAP ya dice cuánto colgajo queda; acá solo si se repuso.
  { k: 'colgajoReposicion', label: '¿Está repuesto el colgajo?', tipo: 'select', nivel: 'esencial',
    opciones: ['Colgajo repuesto sobre el lecho', 'Colgajo aún sin reponer'],
    aplica: (l) => l.tipo === 'desgarro' && (l.categoria === 'Tipo 1' || l.categoria === 'Tipo 2'), prosa: (v) => m(v) },
  { k: 'daiExposicion', label: 'Expuesta a', tipo: 'select', nivel: 'detalle',
    opciones: ['Orina', 'Deposiciones', 'Orina y deposiciones'],
    aplica: (l) => l.tipo === 'DAI', prosa: (v) => `expuesta a ${m(v)}` },
  // Panel GLOBIAD-M (IAD 2026, pág. 24). En 1B (piel sin pérdida) solo
  // caben los dos primeros: los demás son del lecho de una 2B.
  { k: 'daiSignosB', label: 'Signos que sustentan la B', tipo: 'multi', nivel: 'esencial',
    opciones: ['Lesiones satélite', 'Descamación blanquecina', 'Cambio de color del lecho', 'Exudado purulento', 'Exudado excesivo'],
    opcionesDe: (l) => (l.categoria === 'Categoría 1B' ? ['Lesiones satélite', 'Descamación blanquecina'] : null),
    aplica: (l) => l.tipo === 'DAI' && /B$/.test(l.categoria || ''), prosa: (v) => `con ${enLista(v.map(m))}` },
  { k: 'quemFlictenas', label: 'Flictenas', tipo: 'select', nivel: 'esencial',
    opciones: ['Con flictenas', 'Sin flictenas'], aplica: (l) => l.tipo === 'quemadura', prosa: (v) => m(v) },
  { k: 'quemCircunferencial', label: '¿Rodea el segmento?', tipo: 'select', nivel: 'detalle',
    opciones: ['Circunferencial', 'No circunferencial'], aplica: (l) => l.tipo === 'quemadura', prosa: (v) => m(v) },
  { k: 'quemMetodo', label: 'Extensión calculada con', tipo: 'select', nivel: 'detalle',
    opciones: ['Palma con dedos', 'Regla de los 9', 'Lund y Browder'], aplica: (l) => l.tipo === 'quemadura', prosa: null },
  { k: 'venosaPiel', label: 'Piel de la pierna (CEAP)', tipo: 'multi', nivel: 'esencial',
    opciones: ['Edema (C3)', 'Pigmentación (C4a)', 'Eccema (C4a)', 'Lipodermatoesclerosis (C4b)', 'Atrofia blanca (C4b)', 'Corona flebectásica (C4c)'],
    aplica: (l) => l.tipo === 'ulcera_venosa', prosa: (v) => `pierna con ${enLista(v.map(m))}` },
  { k: 'arterialDolorReposo', label: 'Dolor en reposo', tipo: 'select', nivel: 'esencial',
    opciones: ['Con dolor en reposo', 'Sin dolor en reposo'], aplica: (l) => l.tipo === 'ulcera_arterial', prosa: (v) => m(v) },
  { k: 'gangrena', label: 'Gangrena', tipo: 'select', nivel: 'esencial',
    opciones: ['Sin gangrena', 'Gangrena seca', 'Gangrena húmeda'], aplica: (l) => l.tipo === 'ulcera_arterial' || l.tipo === 'pie_diabetico', prosa: (v) => m(v) },
  { k: 'pulsos', label: 'Pulsos del pie', tipo: 'select', nivel: 'esencial',
    opciones: ['Presentes', 'Disminuidos', 'Ausentes', 'No evaluados'], aplica: (l) => l.tipo === 'ulcera_arterial' || l.tipo === 'ulcera_venosa' || l.tipo === 'pie_diabetico',
    prosa: (v) => (v === 'No evaluados' ? 'pulsos del pie no evaluados' : `pulsos del pie ${m(v)}`) },
  { k: 'pieTemperatura', label: 'Temperatura del pie', tipo: 'select', nivel: 'detalle',
    opciones: ['Pie frío', 'Temperatura conservada'], aplica: (l) => l.tipo === 'ulcera_arterial' || l.tipo === 'pie_diabetico', prosa: (v) => m(v) },
  // SINBAD (IWGDF 2023): cada variable por separado, nunca solo el puntaje.
  { k: 'sinbadSitio', label: 'Sitio', tipo: 'select', nivel: 'esencial',
    opciones: ['Antepié', 'Mediopié o retropié'], aplica: (l) => l.tipo === 'pie_diabetico', prosa: (v) => `en ${m(v)}` },
  { k: 'sinbadIsquemia', label: 'Isquemia', tipo: 'select', nivel: 'esencial',
    opciones: ['Al menos un pulso palpable', 'Evidencia clínica de flujo reducido'], aplica: (l) => l.tipo === 'pie_diabetico', prosa: (v) => m(v) },
  { k: 'sinbadNeuropatia', label: 'Neuropatía', tipo: 'select', nivel: 'esencial',
    opciones: ['Sensibilidad protectora conservada', 'Sensibilidad protectora perdida', 'No evaluada'], aplica: (l) => l.tipo === 'pie_diabetico',
    prosa: (v) => (v === 'No evaluada' ? 'sensibilidad protectora no evaluada' : m(v)) },
  { k: 'sinbadArea', label: 'Área', tipo: 'select', nivel: 'esencial',
    opciones: ['Menor de 1 cm²', '1 cm² o más'], aplica: (l) => l.tipo === 'pie_diabetico', prosa: (v) => `área ${m(v)}` },
  { k: 'sinbadProfundidad', label: 'Hasta dónde llega', tipo: 'select', nivel: 'esencial',
    opciones: ['Piel y subcutáneo', 'Músculo o tendón', 'Hueso o articulación'], aplica: (l) => l.tipo === 'pie_diabetico', prosa: (v) => `compromete ${m(v)}` },
  { k: 'sondaHueso', label: 'Prueba de sonda a hueso', tipo: 'select', nivel: 'detalle',
    opciones: ['Positiva', 'Negativa', 'No realizada'], aplica: (l) => l.tipo === 'pie_diabetico',
    prosa: (v) => (v === 'No realizada' ? null : `sonda a hueso ${m(v)}`) },
  { k: 'pieTipo', label: 'Tipo, según el equipo', tipo: 'select', nivel: 'detalle',
    opciones: ['Neuropática', 'Neuroisquémica', 'Isquémica'], aplica: (l) => l.tipo === 'pie_diabetico', prosa: (v) => `de tipo ${m(v)} según el equipo` },
  // La gradúa quien diagnostica: acá solo se transcribe si quedó documentada.
  { k: 'iwgdfInfeccion', label: 'Infección IWGDF/IDSA documentada por el equipo', tipo: 'select', nivel: 'detalle',
    opciones: ['Grado 1, no infectada', 'Grado 2, leve', 'Grado 3, moderada', 'Grado 4, grave'], aplica: (l) => l.tipo === 'pie_diabetico',
    prosa: (v) => `infección IWGDF/IDSA ${m(v)}, documentada por el equipo` },
  { k: 'callo', label: 'Callo alrededor', tipo: 'select', nivel: 'detalle',
    opciones: ['Con callo', 'Sin callo'], aplica: (l) => l.tipo === 'pie_diabetico', prosa: (v) => m(v) },
  { k: 'aposito', label: 'Apósito', tipo: 'select', nivel: 'esencial',
    opciones: ['Limpio y seco', 'Manchado escaso', 'Manchado, se cambia', 'Sin apósito'], aplica: (l) => l.tipo === 'operatoria', prosa: (v) => `apósito ${m(v)}` },
  { k: 'aspecto', label: 'Qué se ve', tipo: 'multi', nivel: 'esencial',
    opciones: ['Eritema que blanquea', 'Eritema que no blanquea', 'Coloración violácea o granate', 'Ampolla serosa', 'Ampolla hemática', 'Pérdida superficial de piel', 'Pérdida profunda de piel', 'Costra', 'Escara'],
    aplica: (l) => l.tipo === 'por_definir', prosa: (v) => m(enLista(v)) },
  // Equimosis y hematoma (fuentes en docs/piel/fuentes.md: MedlinePlus,
  // DermNet, StatPearls, póster NPIAP "DTPI or an imposter?"). El color se
  // describe y JAMÁS se usa para datar: ninguna fuente revisada permite
  // estimar la antigüedad por el color (Maguire 2005, Mosqueda 2005).
  { k: 'numero', label: 'Cuántas', tipo: 'select', nivel: 'esencial',
    opciones: ['Única', 'Varias'], aplica: (l) => l.tipo === 'equimosis' || l.tipo === 'hematoma', prosa: (v) => (v === 'Varias' ? 'varias' : 'única') },
  { k: 'blanquea', label: '¿Blanquea a la presión?', tipo: 'select', nivel: 'esencial',
    opciones: ['No blanquea a la presión', 'Blanquea a la presión'], aplica: (l) => l.tipo === 'equimosis', prosa: (v) => m(v) },
  { k: 'pielIntegra', label: 'La piel encima', tipo: 'select', nivel: 'esencial',
    opciones: ['Piel intacta', 'Con flictena', 'Con erosión o herida', 'Con sangrado activo'], aplica: (l) => l.tipo === 'equimosis' || l.tipo === 'hematoma',
    prosa: (v) => (v === 'Piel intacta' ? 'piel intacta' : `piel ${m(v)}`) },
  { k: 'colores', label: 'Colores que ves', tipo: 'multi', nivel: 'esencial',
    opciones: ['Rojo', 'Violáceo', 'Azulado', 'Verdoso', 'Amarillento', 'Café'], aplica: (l) => l.tipo === 'equimosis' || l.tipo === 'hematoma', prosa: (v) => `de color ${enLista(v.map(m))}` },
  { k: 'volumen', label: 'Aumento de volumen', tipo: 'select', nivel: 'esencial',
    opciones: ['Sin aumento de volumen palpable', 'Con aumento de volumen', 'No evaluado'], aplica: (l) => l.tipo === 'equimosis' || l.tipo === 'hematoma',
    prosa: (v) => (v === 'No evaluado' ? 'aumento de volumen no evaluado' : m(v)) },
  { k: 'consistencia', label: 'Consistencia', tipo: 'select', nivel: 'detalle',
    opciones: ['Blanda', 'Firme', 'Tensa', 'Fluctuante'], aplica: (l) => l.tipo === 'hematoma', prosa: (v) => `consistencia ${m(v)}` },
  { k: 'pulsatil', label: '¿Late?', tipo: 'select', nivel: 'esencial',
    opciones: ['No late', 'Late a la palpación'], aplica: (l) => l.tipo === 'hematoma', prosa: (v) => (v === 'No late' ? 'no pulsátil' : 'pulsátil a la palpación') },
  { k: 'temperaturaLocal', label: 'Temperatura comparada con la piel vecina', tipo: 'select', nivel: 'detalle',
    opciones: ['Igual', 'Más caliente', 'Más fría'], aplica: (l) => l.tipo === 'equimosis' || l.tipo === 'hematoma',
    prosa: (v) => (v === 'Igual' ? 'temperatura igual a la piel vecina' : `${m(v)} que la piel vecina`) },
  { k: 'dolorPalpacion', label: 'Dolor', tipo: 'select', nivel: 'esencial',
    opciones: ['Sin dolor a la palpación', 'Dolor a la palpación', 'Dolor que no calza con lo que se ve'], aplica: (l) => l.tipo === 'equimosis' || l.tipo === 'hematoma',
    prosa: (v) => m(v) },
  { k: 'cambio', label: 'Respecto de la valoración anterior', tipo: 'select', nivel: 'esencial',
    opciones: ['Primera valoración', 'Más grande', 'Igual', 'Más pequeño'], aplica: (l) => l.tipo === 'equimosis' || l.tipo === 'hematoma',
    prosa: (v) => (v === 'Primera valoración' ? null : `${m(v)} que en la valoración anterior`) },
  { k: 'anticoagulacion', label: '¿Recibe anticoagulante o antiagregante?', tipo: 'select', nivel: 'esencial',
    opciones: ['Sí', 'No', 'No se sabe'], aplica: (l) => l.tipo === 'equimosis' || l.tipo === 'hematoma',
    prosa: (v) => (v === 'Sí' ? 'en tratamiento anticoagulante o antiagregante' : v === 'No' ? 'sin anticoagulante ni antiagregante' : 'uso de anticoagulante sin confirmar') },
  { k: 'neurovascular', label: 'Distal a la lesión', tipo: 'select', nivel: 'detalle',
    opciones: ['Pulso, color, temperatura y sensibilidad conservados', 'Con alteración de pulso, color, temperatura o sensibilidad', 'No evaluado'],
    aplica: (l) => l.tipo === 'hematoma' && ['miembro_superior', 'miembro_inferior', 'perine'].includes(l.ubicacion?.region),
    prosa: (v) => (v === 'No evaluado' ? 'estado distal no evaluado'
      : v.startsWith('Con alteración') ? 'con alteración distal de pulso, color, temperatura o sensibilidad'
        : 'pulso, color, temperatura y sensibilidad distales conservados') },
];

// ── Temporalidad ──────────────────────────────────────────────────────────────
// Desde cuándo está DOCUMENTADA, no desde cuándo existe. Detectarla en el
// turno no prueba que se haya originado en el turno ni en el establecimiento.
export const PRESENTE_DESDE = [
  { id: 'ingreso_hospital', label: 'Presente al ingreso al hospital', prosa: 'presente al ingreso hospitalario' },
  { id: 'ingreso_servicio', label: 'Presente al ingreso al servicio', prosa: 'presente al ingreso al servicio' },
  { id: 'recibir_turno', label: 'Presente al recibir el turno', prosa: 'presente al recibir' },
  { id: 'detectada_turno', label: 'La detecté durante mi turno', prosa: 'detectada en el turno' },
];
export const APARICION = ['Desconocida', 'Conocida'];

// Procedencia de cada valoración: en qué momento se miró.
export const PROCEDENCIA = [
  { id: 'recibir', label: 'Al recibir el turno' },
  { id: 'control', label: 'Control durante el turno' },
  { id: 'curacion', label: 'En la curación' },
  { id: 'deteccion', label: 'Al detectarla' },
];

// Lo que se hizo y lo que queda.
export const COBERTURAS = ['Espuma de silicona', 'Espuma hidrocelular', 'Hidrocoloide', 'Alginato', 'Hidrofibra', 'Hidrogel', 'Malla de silicona', 'Apósito de plata', 'Gasa vaselinada', 'Película transparente', 'Sin cobertura'];
export const INTERVENCIONES = ['Limpieza de la herida', 'Protección de la piel perilesional', 'Alivio de presión o reposicionamiento', 'Descarga o reposición del dispositivo', 'Manejo de la humedad', 'Curación según protocolo'];
export const TOLERANCIA = ['Bien tolerada', 'Con dolor', 'Requirió suspender'];
export const PENDIENTES = ['Próxima curación', 'Revalorar en el próximo turno', 'Evaluación por enfermera de heridas', 'Aviso al médico, respuesta pendiente', 'Cultivo según indicación'];

// Motivos de un cambio de clasificación. Mejorar no es uno: una LPP que sana
// conserva su categoría y se marca "en cicatrización".
export const MOTIVO_RECLASIFICACION = [
  { id: 'progresion', label: 'Progresó', prosa: 'por progresión' },
  { id: 'nueva_informacion', label: 'Se pudo ver la extensión del daño', prosa: 'al poder valorar la extensión del daño' },
  { id: 'correccion', label: 'Corrijo un error de registro', prosa: 'por corrección del registro' },
];

// ── Qué se pregunta según la lesión ───────────────────────────────────────────
export function esMucosa(l) { return normalizarUbicacion(l?.ubicacion)?.tejido === 'mucosa'; }

// Herida abierta de espesor total o crónica: tiene lecho que describir en
// porcentajes, profundidad y túneles.
export function esAbierta(l) {
  const t = l?.tipo;
  if (t === 'LPP') return ['Categoría III', 'Categoría IV', 'No clasificable'].includes(CATEGORIA_LEGADO[l.categoria] || l.categoria);
  if (t === 'ulcera_venosa' || t === 'ulcera_arterial' || t === 'pie_diabetico') return true;
  if (t === 'operatoria') return l.estadoHerida === 'Dehiscencia parcial' || l.estadoHerida === 'Dehiscencia total';
  if (t === 'quemadura') return l.categoria === 'Tipo AB-B' || l.categoria === 'Tipo B';
  return false;
}

// Con pérdida de piel (aunque sea superficial) o con categoría aún sin saber:
// se puede describir exudado y bordes. Ante la duda se OFRECE la pregunta (no
// se responde por nadie): preguntar de más no inventa nada.
export function conPerdidaDePiel(l) {
  if (esAbierta(l)) return true;
  const t = l?.tipo;
  if (t === 'LPP') return esMucosa(l) || l.categoria === 'Categoría II' || !l.categoria || l.categoria === PENDIENTE;
  if (t === 'DAI') return !l.categoria || l.categoria === PENDIENTE || /^Categoría 2/.test(l.categoria);
  if (t === 'MARSI') return l.subtipo !== 'Foliculitis';
  return ['desgarro', 'humedad', 'operatoria', 'quemadura', 'por_definir', 'hematoma'].includes(t);
}

// La clasificación orienta qué preguntar, pero no elimina lo observado.
// Una lesión por definir o una LPP sin categoría puede describirse antes de
// clasificarla. Los bloques ya escritos siguen visibles para poder revisarlos.
export function camposDe(l, v = null) {
  const t = l?.tipo;
  const abierta = esAbierta(l);
  const perdida = conPerdidaDePiel(l);
  const sangre = t === 'equimosis' || t === 'hematoma';
  const porClasificar = t === 'por_definir' || (t === 'LPP' && !esMucosa(l) && (!l.categoria || l.categoria === PENDIENTE));
  const campos = {
    medidas: true,
    profundidad: abierta || porClasificar,
    tunel: abierta || porClasificar,
    // En mucosa también se describe lo que se ve en el lecho (el coágulo
    // blando que parece esfacelo va como "otro tejido").
    tejido: abierta || porClasificar || (t === 'LPP' && esMucosa(l)),
    estructuras: porClasificar || (t === 'LPP' && (l.categoria === 'Categoría IV' || l.categoria === 'Categoría III' || l.categoria === 'No clasificable')),
    exudado: perdida && !sangre && t !== 'equimosis',
    bordes: abierta || porClasificar || (t === 'LPP' && l.categoria === 'Categoría II') || t === 'desgarro' || t === 'operatoria',
    perilesional: !sangre,
    // Los signos son hallazgos; ofrecerlos no presupone una herida abierta.
    infeccion: perdida || ['DAI', 'MARSI', 'humedad', 'desgarro'].includes(t) || esMucosa(l),
    dolor: true,
    itb: t === 'ulcera_venosa' || t === 'ulcera_arterial' || t === 'pie_diabetico',
    extension: t === 'quemadura',
    simples: CAMPOS_SIMPLES.filter((c) => c.aplica(l)),
    cobertura: !sangre,
  };
  // Solo la valoración que se está editando o narrando, nunca otra anterior.
  // Se conserva también el borrador incompleto para que la validación pueda
  // avisar: cambiar de categoría no debe ocultar un porcentaje inválido.
  if (v?.medidas?.profundidad !== undefined) campos.profundidad = true;
  for (const bloque of ['tejido', 'tunel', 'estructuras', 'exudado', 'bordes', 'perilesional', 'infeccion']) {
    if (v?.[bloque] && Object.keys(v[bloque]).length) campos[bloque] = true;
  }
  return campos;
}

// Las opciones que se ofrecen de un campo para ESTA lesión (algunas dependen
// de la categoría, como los signos de la DAI 1B).
export function opcionesCampo(c, l) { return (c.opcionesDe && c.opcionesDe(l)) || c.opciones; }

// Conserva las observaciones generales aunque cambie la clasificación.
// Los campos específicos de cada tipo siguen limitados a su catálogo.
// Una incompatibilidad clínica se advierte con pistasLesion, no borrando datos.
export function ajustarValoracionALesion(v, l) {
  if (!v || typeof v !== 'object') return v;
  const c = camposDe(l, v);
  const out = { ...v };
  for (const campo of CAMPOS_SIMPLES) {
    if (out[campo.k] === undefined) continue;
    if (!campo.aplica(l)) { delete out[campo.k]; continue; }
    const ops = opcionesCampo(campo, l);
    if (campo.tipo === 'multi') { const f = out[campo.k].filter((x) => ops.includes(x)); if (f.length) out[campo.k] = f; else delete out[campo.k]; }
    else if (!ops.includes(out[campo.k])) delete out[campo.k];
  }
  if (!c.profundidad && out.medidas?.profundidad !== undefined) { const { profundidad, ...resto } = out.medidas; out.medidas = resto; if (!Object.keys(resto).length) delete out.medidas; }
  for (const [bloque, clave] of [['tejido', 'tejido'], ['tunel', 'tunel'], ['estructuras', 'estructuras'], ['exudado', 'exudado'], ['bordes', 'bordes'], ['perilesional', 'perilesional'], ['infeccion', 'infeccion'], ['itb', 'itb'], ['extension', 'extensionPct']]) {
    if (!c[bloque]) delete out[clave];
  }
  return out;
}

// ── Validación: nada se arregla en silencio ───────────────────────────────────
const NUM_RE = /^\d{1,3}([.,]\d{1,2})?$/;
// '3' | '3,5' | '3.5' → número; cualquier otra cosa → null. No redondea.
export function leerNumero(v) {
  const t = String(v ?? '').trim();
  if (!NUM_RE.test(t)) return null;
  const n = Number(t.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

// Una medida es un número positivo o un ESTADO (no medido, no visible…).
export function medidaValida(v) {
  if (v === undefined || v === null || v === '') return true;
  if (v === ESTADO.NO_MEDIDO || v === ESTADO.NO_VISIBLE || v === ESTADO.NO_APLICA) return true;
  const n = leerNumero(v);
  return n !== null && n > 0 && n <= 100;
}

// Los porcentajes del lecho: devuelve el diagnóstico, no los corrige.
//   { vacio, suma, completo, estado, problemas: [...] }
// `estado` (no visible / no evaluado) reemplaza a los porcentajes.
export function validarTejido(t) {
  if (!t || typeof t !== 'object') return { vacio: true, suma: 0, completo: false, problemas: [] };
  if (esEstado(t.estado)) return { vacio: false, suma: 0, completo: true, estado: t.estado, problemas: [] };
  const problemas = [];
  let suma = 0;
  let alguno = false;
  for (const { k, label } of TEJIDOS_LECHO) {
    const v = t[k];
    if (v === undefined || v === null || v === '') continue;
    alguno = true;
    const n = leerNumero(v);
    if (n === null || n < 0 || n > 100 || !Number.isInteger(n)) {
      problemas.push(`${label}: "${v}" no es un porcentaje entero entre 0 y 100.`);
      continue;
    }
    suma += n;
  }
  if (!alguno) return { vacio: true, suma: 0, completo: false, problemas };
  if (!problemas.length && suma !== 100) problemas.push(suma < 100 ? `Los porcentajes suman ${suma}% y faltan ${100 - suma}%.` : `Los porcentajes suman ${suma}% y sobran ${suma - 100}%.`);
  return { vacio: false, suma, completo: !problemas.length && suma === 100, problemas };
}

// Reparto asistido, EXPLÍCITO: la UI lo ofrece como botón con el número a la
// vista ("Asignar el 10% que falta a granulación") y solo se aplica si la
// persona lo toca. Devuelve una copia nueva, no toca la original.
export function asignarRestoTejido(t, k) {
  const v = validarTejido(t);
  if (v.vacio || v.estado || v.suma >= 100 || v.problemas.some((p) => !/faltan/.test(p)) || !TEJIDOS_LECHO.some((x) => x.k === k)) return t;
  const actual = leerNumero(t[k]) || 0;
  return { ...t, [k]: String(actual + (100 - v.suma)) };
}

const ITB_RE = /^[0-2]([.,]\d{1,2})?$/;
// Problemas que impiden guardar, con su motivo. No se guarda un dato que la
// persona no escribió así.
export function problemasValoracion(v) {
  const out = [];
  const md = v?.medidas || {};
  for (const [k, label] of [['largo', 'Largo'], ['ancho', 'Ancho'], ['profundidad', 'Profundidad']]) {
    if (!medidaValida(md[k])) out.push(`${label}: "${md[k]}" no es una medida en centímetros (usa un número como 3 o 2,5).`);
  }
  const t = validarTejido(v?.tejido);
  if (!t.vacio) out.push(...t.problemas);
  const tu = v?.tunel || {};
  for (const [k, label] of [['socavamientoCm', 'Socavamiento'], ['tunelCm', 'Túnel']]) {
    if (tu[k] !== undefined && tu[k] !== '' && !medidaValida(tu[k])) out.push(`${label}: la extensión tiene que ser un número en centímetros.`);
  }
  const eva = v?.dolor?.eva;
  if (eva !== undefined && eva !== '' && eva !== ESTADO.NO_EVALUADO) {
    const n = leerNumero(eva);
    if (n === null || !Number.isInteger(n) || n < 0 || n > 10) out.push('Dolor: la EVA va de 0 a 10.');
  }
  if (v?.itb !== undefined && v.itb !== '' && !esEstado(v.itb) && !ITB_RE.test(String(v.itb).trim())) out.push('ITB: escribe el índice como 0,8 o 1,1.');
  const ext = v?.extensionPct;
  if (ext !== undefined && ext !== '') {
    const n = leerNumero(ext);
    if (n === null || n <= 0 || n > 100) out.push('Extensión: un porcentaje de superficie corporal entre 0 y 100.');
  }
  return out;
}

// ── Construcción de la lesión y su historia ──────────────────────────────────
function idNuevo(prefijo) {
  return `${prefijo}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}
function horaDe(d) {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
const HORA_RE = /^\d{1,2}:\d{2}$/;
const FECHA_RE = /^\d{4}-\d{2}-\d{2}$/;

const unoDe = (v, lista) => (lista.includes(v) ? v : undefined);
const variosDe = (v, lista) => (Array.isArray(v) ? [...new Set(v.filter((x) => lista.includes(x)))] : undefined);
const sinVacios = (o) => {
  const out = {};
  for (const [k, v] of Object.entries(o)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v) && !v.length) continue;
    if (typeof v === 'object' && !Array.isArray(v) && !Object.keys(v).length) continue;
    out[k] = v;
  }
  return out;
};
// Medida guardada: tal cual la escribió la persona (con coma) o un estado.
function medidaGuardada(v) {
  if (v === undefined || v === null || v === '') return undefined;
  if (v === ESTADO.NO_MEDIDO || v === ESTADO.NO_VISIBLE || v === ESTADO.NO_APLICA) return v;
  const n = leerNumero(v);
  return n !== null && n > 0 && n <= 100 ? String(v).trim().replace('.', ',') : undefined;
}
function relojValido(v) {
  const n = Number(v);
  return Number.isInteger(n) && n >= 1 && n <= 12 ? n : undefined;
}

// Limpia una valoración a catálogo cerrado. Lo que no calza se DESCARTA (no se
// convierte en otro valor) y lo vacío no se guarda. La UI no deja guardar con
// problemas (problemasValoracion), así que esto es la red para datos que
// lleguen por otra vía.
export function normalizarValoracion(v, { ahora = new Date() } = {}) {
  const fecha = ahora instanceof Date ? ahora : new Date(ahora);
  const md = v?.medidas || {};
  const t = v?.tejido || {};
  const tejOk = validarTejido(t);
  const tej = esEstado(t.estado)
    ? { estado: t.estado }
    // Porcentajes que no suman 100 no se guardan: no se reparten solos.
    : tejOk.completo
      ? sinVacios(Object.fromEntries(TEJIDOS_LECHO.map(({ k }) => [k, leerNumero(t[k]) ? String(leerNumero(t[k])) : undefined])))
      : undefined;
  const tu = v?.tunel || {};
  const inf = v?.infeccion || {};
  const d = v?.dolor || {};
  const evaN = leerNumero(d.eva);
  const simples = {};
  for (const c of CAMPOS_SIMPLES) {
    const val = v?.[c.k];
    simples[c.k] = c.tipo === 'multi' ? variosDe(val, c.opciones) : unoDe(val, c.opciones);
  }
  const itb = v?.itb === ESTADO.NO_EVALUADO ? ESTADO.NO_EVALUADO
    : (ITB_RE.test(String(v?.itb ?? '').trim()) ? String(v.itb).trim().replace('.', ',') : undefined);
  const extN = leerNumero(v?.extensionPct);
  return sinVacios({
    id: typeof v?.id === 'string' ? v.id : idNuevo('val'),
    ts: Number.isFinite(v?.ts) ? v.ts : fecha.getTime(),
    hora: HORA_RE.test(v?.hora || '') ? v.hora : horaDe(fecha),
    procedencia: unoDe(v?.procedencia, PROCEDENCIA.map((p) => p.id)),
    medidas: sinVacios({ largo: medidaGuardada(md.largo), ancho: medidaGuardada(md.ancho), profundidad: medidaGuardada(md.profundidad) }),
    convencion: unoDe(v?.convencion, ['cabeza_pies', 'eje_mayor']),
    tejido: tej,
    tunel: sinVacios({
      socavamiento: unoDe(tu.socavamiento, ['presente', ESTADO.AUSENTE, ESTADO.NO_EVALUADO]),
      socavamientoDesde: tu.socavamiento === 'presente' ? relojValido(tu.socavamientoDesde) : undefined,
      socavamientoHasta: tu.socavamiento === 'presente' ? relojValido(tu.socavamientoHasta) : undefined,
      socavamientoCm: tu.socavamiento === 'presente' ? medidaGuardada(tu.socavamientoCm) : undefined,
      tunel: unoDe(tu.tunel, ['presente', ESTADO.AUSENTE, ESTADO.NO_EVALUADO]),
      tunelReloj: tu.tunel === 'presente' ? relojValido(tu.tunelReloj) : undefined,
      tunelCm: tu.tunel === 'presente' ? medidaGuardada(tu.tunelCm) : undefined,
    }),
    estructuras: sinVacios({ visibles: variosDe(v?.estructuras?.visibles, ESTRUCTURAS), palpables: variosDe(v?.estructuras?.palpables, ESTRUCTURAS) }),
    exudado: sinVacios({
      cantidad: unoDe(v?.exudado?.cantidad, [...EXUDADO_CANTIDAD, ESTADO.NO_EVALUADO]),
      tipo: v?.exudado?.cantidad && !['Sin exudado', ESTADO.NO_EVALUADO].includes(v.exudado.cantidad) ? unoDe(v?.exudado?.tipo, EXUDADO_TIPO) : undefined,
      olor: unoDe(v?.exudado?.olor, ['Sin mal olor tras limpiar', 'Mal olor tras limpiar', ESTADO.NO_EVALUADO]),
    }),
    bordes: variosDe(v?.bordes, BORDES),
    perilesional: (() => {
      const lista = variosDe(v?.perilesional, PERILESIONAL);
      // "Sana" es excluyente: con otro hallazgo no puede ser sana.
      return lista && lista.includes('Sana') && lista.length > 1 ? lista.filter((x) => x !== 'Sana') : lista;
    })(),
    infeccion: sinVacios({
      signos: variosDe(inf.signos, SIGNOS_INFECCION_TODOS),
      // "Sin signos" solo si se miró y no hay ninguno marcado.
      estado: !inf.signos?.length ? unoDe(inf.estado, [ESTADO.AUSENTE, ESTADO.NO_EVALUADO]) : undefined,
      interpretacion: unoDe(inf.interpretacion, INTERPRETACION_INFECCION),
    }),
    dolor: sinVacios({
      eva: d.eva === ESTADO.NO_EVALUADO ? ESTADO.NO_EVALUADO : (evaN !== null && Number.isInteger(evaN) && evaN >= 0 && evaN <= 10 ? String(evaN) : undefined),
      momento: unoDe(d.momento, DOLOR_MOMENTO),
    }),
    itb,
    extensionPct: extN !== null && extN > 0 && extN <= 100 ? String(v.extensionPct).trim().replace('.', ',') : undefined,
    trayectoria: unoDe(v?.trayectoria, TRAYECTORIA),
    ...simples,
  });
}

// ¿La valoración trae algún hallazgo? (más allá de su id, hora y procedencia)
export function valoracionConDatos(v) {
  // eslint-disable-next-line no-unused-vars
  const { id, ts, hora, procedencia, convencion, ...resto } = v || {};
  return Object.keys(resto).length > 0;
}

// Atributos propios de la LESIÓN (no cambian en cada valoración).
function atributosLesion(d) {
  const tipo = TIPOS.some((t) => t.id === d.tipo) ? d.tipo : 'por_definir';
  const def = CLASIFICACION[tipo];
  const ubic = normalizarUbicacion(d.ubicacion);
  const mucosa = ubic?.tejido === 'mucosa';
  const out = { tipo, ubicacion: ubic };
  if (def) {
    const v = d[def.campo];
    // LPP en mucosa: no se categoriza (guía internacional 2026, CL2; NT 234).
    if (!(tipo === 'LPP' && mucosa)) out[def.campo] = v === PENDIENTE ? PENDIENTE : unoDe(v, def.opciones);
  }
  if (tipo === 'LPP') out.dispositivo = unoDe(d.dispositivo, DISPOSITIVOS_LPP);
  if (tipo === 'desgarro') out.mecanismo = unoDe(d.mecanismo, MECANISMO_DESGARRO);
  if (tipo === 'MARSI' || (tipo === 'desgarro' && d.mecanismo === 'Retiro de un adhesivo')) out.producto = unoDe(d.producto, PRODUCTO_ADHESIVO);
  if (tipo === 'MARSI') out.persiste30 = unoDe(d.persiste30, PERSISTE_30);
  if (tipo === 'MARSI' && d.subtipo === 'Dermatitis de contacto') out.certeza = unoDe(d.certeza, CERTEZA_DERMATITIS);
  if (tipo === 'operatoria') out.cierre = unoDe(d.cierre, CIERRE_OPERATORIA);
  if (tipo === 'equimosis' || tipo === 'hematoma') {
    out.contexto = variosDe(d.contexto, CONTEXTO_SANGRE);
    out.fuenteContexto = out.contexto?.length ? unoDe(d.fuenteContexto, FUENTE_CONTEXTO) : undefined;
  }
  return out;
}

function origenNormalizado(o) {
  const presente = unoDe(o?.presenteDesde, PRESENTE_DESDE.map((p) => p.id));
  const aparicion = unoDe(o?.aparicion, APARICION);
  return sinVacios({
    presenteDesde: presente,
    aparicion,
    aparicionFecha: aparicion === 'Conocida' && FECHA_RE.test(o?.aparicionFecha || '') ? o.aparicionFecha : undefined,
  });
}

// Lo que impide crear la lesión: sin ubicación ni tipo no hay registro.
export function problemasLesion(borrador) {
  const out = [];
  if (!normalizarUbicacion(borrador?.ubicacion)) out.push('Falta la ubicación.');
  if (!TIPOS.some((t) => t.id === borrador?.tipo)) out.push('Falta elegir qué lesión es (puede ser "Lesión por definir").');
  if (!PRESENTE_DESDE.some((p) => p.id === borrador?.origen?.presenteDesde)) out.push('Falta decir desde cuándo está documentada.');
  out.push(...problemasValoracion(borrador?.valoracion));
  return out;
}

// Crea una lesión con su primera valoración. `presenteAlIngreso` se sigue
// escribiendo para las funciones que ya lo leen, con su significado de
// siempre en la app: estaba al recibir (true) o se detectó en el turno
// (false). No dice nada sobre dónde ni cuándo se originó.
const idValido = (x) => typeof x === 'string' && /^pl-[a-z0-9-]{4,40}$/.test(x);

export function crearLesion(borrador, { ahora = new Date() } = {}) {
  const fecha = ahora instanceof Date ? ahora : new Date(ahora);
  const attrs = atributosLesion(borrador || {});
  const origen = origenNormalizado(borrador?.origen);
  const detectada = origen.presenteDesde === 'detectada_turno';
  const valoracion = ajustarValoracionALesion(normalizarValoracion({ ...(borrador?.valoracion || {}), procedencia: borrador?.valoracion?.procedencia || (detectada ? 'deteccion' : 'recibir') }, { ahora: fecha }), attrs);
  const lesion = sinVacios({
    id: idNuevo('pl'),
    v: VERSION_LESION,
    ...attrs,
    zona: zonaDeUbicacion(attrs.ubicacion),
    origen,
    presenteAlIngreso: !detectada,
    hora: horaDe(fecha),
    ts: fecha.getTime(),
    valoraciones: [valoracion],
    historial: [{ evento: 'creacion', ts: fecha.getTime(), hora: horaDe(fecha), cobertura: unoDe(borrador?.cobertura, COBERTURAS) || null }],
    cobertura: unoDe(borrador?.cobertura, COBERTURAS),
    pendientes: variosDe(borrador?.pendientes, PENDIENTES),
    // La misma lesión registrada en el turno anterior (solo su id): permite
    // seguirla entre turnos sin copiar sus hallazgos.
    continuaDe: idValido(borrador?.continuaDe) ? borrador.continuaDe : undefined,
    // La serie: el id de la PRIMERA vez que se registró esta lesión. Viaja de
    // turno en turno (la retención borra el registro de ayer y con él la
    // cadena de continuaDe), así una lesión se reconoce como la misma aunque
    // pasen tres turnos. Sin continuidad, la serie es la propia id.
    serie: idValido(borrador?.continuaDe) ? (idValido(borrador?.serie) ? borrador.serie : borrador.continuaDe) : undefined,
  });
  // `lado` es null y no ausente en el registro histórico: se conserva así.
  lesion.lado = attrs.ubicacion?.lado || null;
  lesion.presenteAlIngreso = !detectada;
  return lesion;
}

export function esV2(l) { return l?.v === VERSION_LESION; }
// El id estable de la lesión entre turnos (ver `serie` en crearLesion).
export function serieDe(l) { return idValido(l?.serie) ? l.serie : l?.id; }
export function valoracionesDe(l) { return Array.isArray(l?.valoraciones) ? l.valoraciones : []; }
export function ultimaValoracion(l) {
  const vs = valoracionesDe(l);
  return vs.length ? vs.reduce((a, b) => ((b.ts || 0) >= (a.ts || 0) ? b : a)) : null;
}
export function primeraValoracion(l) {
  const vs = valoracionesDe(l);
  return vs.length ? vs.reduce((a, b) => ((b.ts || 0) < (a.ts || 0) ? b : a)) : null;
}

// Agrega una valoración NUEVA. Empieza vacía por diseño: la UI muestra la
// anterior como referencia y la persona vuelve a mirar.
export function conValoracion(lesion, valoracion, { ahora = new Date() } = {}) {
  const v = ajustarValoracionALesion(normalizarValoracion({ ...valoracion, id: undefined, ts: undefined, hora: undefined }, { ahora }), lesion);
  return {
    ...lesion,
    valoraciones: [...valoracionesDe(lesion), v],
    historial: [...(lesion.historial || []), { evento: 'valoracion', ts: v.ts, hora: v.hora, valoracion: v.id }],
  };
}

// Corrige una valoración ya guardada: reemplaza sus datos, conserva su hora y
// procedencia, y deja en la historia que fue una corrección y qué campos
// cambiaron (solo los nombres de los campos, jamás valores en texto libre).
export function corregirValoracion(lesion, valoracionId, cambios, { ahora = new Date() } = {}) {
  const fecha = ahora instanceof Date ? ahora : new Date(ahora);
  const vs = valoracionesDe(lesion);
  const actual = vs.find((v) => v.id === valoracionId);
  if (!actual) return lesion;
  // Se ajusta a la lesión COMO ERA en esa valoración: corregir la de las 08:00
  // de una LPP que a las 10:00 pasó de II a III no puede borrar la ampolla que
  // se registró como II (la III ya no pregunta por ampolla).
  const nueva = ajustarValoracionALesion(normalizarValoracion({ ...cambios, id: actual.id, ts: actual.ts, hora: actual.hora, procedencia: actual.procedencia }, { ahora: fecha }), lesionEn(lesion, actual.ts || 0));
  const claves = new Set([...Object.keys(actual), ...Object.keys(nueva)]);
  const campos = [...claves].filter((k) => !['id', 'ts', 'hora', 'procedencia'].includes(k) && JSON.stringify(actual[k]) !== JSON.stringify(nueva[k]));
  if (!campos.length) return lesion;
  return {
    ...lesion,
    valoraciones: vs.map((v) => (v.id === valoracionId ? nueva : v)),
    historial: [...(lesion.historial || []), { evento: 'correccion', ts: fecha.getTime(), hora: horaDe(fecha), valoracion: valoracionId, campos }],
  };
}

// Corrige atributos de la lesión (ubicación, dispositivo, mecanismo…) dejando
// la traza. No sirve para cambiar la clasificación: eso es `reclasificar`, que
// pide el motivo. Tampoco cambia el tipo (otro tipo es otra lesión).
export function corregirLesion(lesion, cambios, { ahora = new Date() } = {}) {
  const fecha = ahora instanceof Date ? ahora : new Date(ahora);
  const def = CLASIFICACION[lesion.tipo];
  const base = { ...lesion, ...cambios, tipo: lesion.tipo };
  if (def) base[def.campo] = lesion[def.campo];
  const attrs = atributosLesion(base);
  const antes = atributosLesion(lesion);
  const campos = Object.keys({ ...attrs, ...antes }).filter((k) => JSON.stringify(attrs[k]) !== JSON.stringify(antes[k]));
  const origen = cambios.origen ? origenNormalizado(cambios.origen) : lesion.origen;
  if (JSON.stringify(origen) !== JSON.stringify(lesion.origen)) campos.push('origen');
  if (!campos.length) return lesion;
  const detectada = origen?.presenteDesde === 'detectada_turno';
  // Lo que deja de aplicar se retira (una LPP movida a mucosa pierde su
  // categoría: en mucosa no se categoriza). La clasificación retirada queda en
  // la traza, para que se sepa qué decía.
  const retirada = def && lesion[def.campo] && attrs[def.campo] === undefined ? { [def.campo]: lesion[def.campo] } : null;
  const out = sinVacios({
    ...lesion,
    ...attrs,
    origen,
    zona: zonaDeUbicacion(attrs.ubicacion),
    historial: [...(lesion.historial || []), { evento: 'correccion', ts: fecha.getTime(), hora: horaDe(fecha), campos, ...(retirada ? { retirado: retirada } : {}) }],
  });
  for (const k of Object.keys(antes)) if (!(k in attrs)) delete out[k];
  out.lado = attrs.ubicacion?.lado || null;
  out.presenteAlIngreso = !detectada;
  return out;
}

// ¿Este cambio de clasificación baja la categoría numérica de una LPP?
export function bajaCategoria(lesion, nueva) {
  return Boolean(lesion?.tipo === 'LPP' && RANGO_CATEGORIA[lesion.categoria] && RANGO_CATEGORIA[nueva]
    && RANGO_CATEGORIA[nueva] < RANGO_CATEGORIA[lesion.categoria]);
}

// Cambia la clasificación conservando la anterior en la historia. Bajar una
// categoría solo se acepta como corrección de un error de registro: una LPP
// que mejora conserva su categoría y se marca en cicatrización.
//   → { lesion, error }
export function reclasificar(lesion, { a, motivo }, { ahora = new Date() } = {}) {
  const fecha = ahora instanceof Date ? ahora : new Date(ahora);
  const def = CLASIFICACION[lesion?.tipo];
  if (!def) return { lesion, error: 'Este tipo de lesión no tiene clasificación.' };
  if (lesion.tipo === 'LPP' && esMucosa(lesion)) return { lesion, error: 'Una LPP en mucosa no se categoriza.' };
  const valor = a === PENDIENTE ? PENDIENTE : unoDe(a, def.opciones);
  if (!valor) return { lesion, error: 'Esa clasificación no existe para este tipo.' };
  const de = lesion[def.campo] || null;
  if (de === valor) return { lesion, error: null };
  // Definir por primera vez una clasificación pendiente no necesita motivo:
  // no hay nada que corregir ni que progresó, es completar el registro.
  const desdePendiente = !de || de === PENDIENTE;
  if (!desdePendiente && !MOTIVO_RECLASIFICACION.some((x) => x.id === motivo)) return { lesion, error: 'Falta el motivo del cambio.' };
  if (bajaCategoria(lesion, valor) && motivo !== 'correccion') {
    return { lesion, error: 'Una LPP que mejora no baja de categoría. Márcala en cicatrización y, si la categoría estaba mal registrada, elige "Corrijo un error de registro".' };
  }
  // Las que no tienen número también tienen su orden. Una no clasificable que
  // deja ver su lecho es III o IV; una en tejidos profundos evoluciona a III,
  // IV o no clasificable, o se resuelve sin pérdida de tejido (eso es
  // cicatrización, no una categoría menor). Pasar a I o II desde ellas, o de
  // III o IV a tejidos profundos, solo como corrección de un error de registro.
  const deC = CATEGORIA_LEGADO[de] || de;
  const haciaMenor = lesion.tipo === 'LPP' && (
    (['No clasificable', 'Tejidos profundos'].includes(deC) && ['Categoría I', 'Categoría II'].includes(valor))
    || (['Categoría III', 'Categoría IV'].includes(deC) && valor === 'Tejidos profundos')
  );
  if (haciaMenor && motivo !== 'correccion') {
    return { lesion, error: deC === 'No clasificable'
      ? 'Una LPP no clasificable que deja ver su lecho es categoría III o IV. Si la categoría estaba mal registrada, elige "Corrijo un error de registro".'
      : 'Ese cambio no es una progresión. Si se resuelve, márcala en cicatrización y, si la categoría estaba mal registrada, elige "Corrijo un error de registro".' };
  }
  return {
    lesion: {
      ...lesion,
      [def.campo]: valor,
      historial: [...(lesion.historial || []), { evento: desdePendiente ? 'clasificacion' : 'reclasificacion', ts: fecha.getTime(), hora: horaDe(fecha), de, a: valor, ...(desdePendiente ? {} : { motivo }) }],
    },
    error: null,
  };
}

export function conCicatrizacion(lesion, enCicatrizacion, { ahora = new Date() } = {}) {
  const fecha = ahora instanceof Date ? ahora : new Date(ahora);
  if (Boolean(lesion?.enCicatrizacion) === Boolean(enCicatrizacion)) return lesion;
  const out = { ...lesion, historial: [...(lesion.historial || []), { evento: enCicatrizacion ? 'cicatrizacion' : 'cicatrizacion_retirada', ts: fecha.getTime(), hora: horaDe(fecha) }] };
  if (enCicatrizacion) out.enCicatrizacion = true; else delete out.enCicatrizacion;
  return out;
}

// Curación: queda con su hora, cobertura, intervenciones y tolerancia.
// `ultimaCuracionHora` y `cobertura` se actualizan para las funciones que ya
// los leen (la entrega, el anclaje de la curación dictada).
export function conCuracion(lesion, { hora, cobertura, intervenciones, tolerancia } = {}, { ahora = new Date() } = {}) {
  const fecha = ahora instanceof Date ? ahora : new Date(ahora);
  const h = HORA_RE.test(hora || '') ? hora : horaDe(fecha);
  const c = sinVacios({ id: idNuevo('cur'), ts: fecha.getTime(), hora: h, cobertura: unoDe(cobertura, COBERTURAS), intervenciones: variosDe(intervenciones, INTERVENCIONES), tolerancia: unoDe(tolerancia, TOLERANCIA) });
  const out = { ...lesion, curaciones: [...(lesion.curaciones || []), c], ultimaCuracionHora: h, historial: [...(lesion.historial || []), { evento: 'curacion', ts: c.ts, hora: h }] };
  if (c.cobertura) out.cobertura = c.cobertura;
  return out;
}

// Los pendientes cambian con su traza (cuáles quedaron, de catálogo).
export function conPendientes(lesion, pendientes, { ahora = new Date() } = {}) {
  const fecha = ahora instanceof Date ? ahora : new Date(ahora);
  const lista = variosDe(pendientes, PENDIENTES) || [];
  if (JSON.stringify(lista) === JSON.stringify(lesion?.pendientes || [])) return lesion;
  const out = { ...lesion, historial: [...(lesion.historial || []), { evento: 'pendientes', ts: fecha.getTime(), hora: horaDe(fecha), pendientes: lista }] };
  if (lista.length) out.pendientes = lista; else delete out.pendientes;
  return out;
}

// ── La frase clínica ──────────────────────────────────────────────────────────
// Solo lo registrado. El orden imita una evolución escrita a mano: qué es y
// dónde, desde cuándo, cuánto mide, cómo se ve, qué la rodea, qué se hizo.
const lista = enLista;

export function nombreLesion(l) {
  const t = l?.tipo;
  const def = CLASIFICACION[t];
  const valor = def ? l?.[def.campo] : null;
  const pendiente = def && (!valor || valor === PENDIENTE);
  if (t === 'LPP') {
    if (esMucosa(l)) return 'LPP en mucosa';
    const c = CATEGORIA_LEGADO[l.categoria] || l.categoria;
    if (!c || c === PENDIENTE) return 'LPP, categoría pendiente';
    if (c === 'No clasificable') return 'LPP no clasificable';
    if (c === 'Tejidos profundos') return 'LPP en tejidos profundos';
    return `LPP ${c.replace('Categoría', 'categoría')}`;
  }
  if (t === 'DAI') return pendiente ? 'DAI, categoría pendiente' : `DAI ${valor.replace('Categoría', 'categoría')} (GLOBIAD)`;
  if (t === 'humedad') {
    if (pendiente) return 'lesión cutánea asociada a humedad';
    if (valor === 'Perilesional') return 'dermatitis perilesional asociada a humedad';
    if (valor === 'Por saliva o mucosidad') return 'dermatitis por saliva o mucosidad';
    if (valor === 'Asociada a exudado de la piel') return 'dermatitis cutánea asociada a exudado';
    return `dermatitis ${m(valor)}`;
  }
  if (t === 'desgarro') {
    const base = pendiente ? 'desgarro cutáneo, tipo pendiente' : `desgarro cutáneo ${m(valor)} (ISTAP)`;
    return l.mecanismo === 'Retiro de un adhesivo' ? `${base}, por adhesivo médico` : base;
  }
  if (t === 'MARSI') {
    if (pendiente) return 'lesión por adhesivo médico';
    if (valor === 'Dermatitis de contacto') {
      if (l.certeza === 'Irritativa, diagnóstico establecido') return 'dermatitis irritativa por contacto con adhesivo médico';
      if (l.certeza === 'Alérgica, diagnóstico establecido') return 'dermatitis alérgica por contacto con adhesivo médico';
      return 'dermatitis de contacto por adhesivo médico, sin determinar si irritativa o alérgica';
    }
    return `lesión por adhesivo médico, ${m(valor)}`;
  }
  if (t === 'operatoria') return 'herida operatoria';
  if (t === 'quemadura') return pendiente ? 'quemadura, profundidad pendiente' : `quemadura ${m(valor)} de Benaim`;
  if (t === 'ulcera_venosa') return pendiente ? 'úlcera venosa' : `úlcera venosa, ${valor}`;
  if (t === 'ulcera_arterial') return 'úlcera arterial';
  if (t === 'pie_diabetico') return 'úlcera de pie diabético';
  if (t === 'equimosis') return 'equimosis';
  if (t === 'hematoma') return 'hematoma';
  return 'lesión de piel, etiología por definir';
}

function fraseMedidas(v) {
  const md = v?.medidas || {};
  const out = [];
  const num = (x) => x && !esEstado(x);
  if (num(md.largo) && num(md.ancho)) out.push(`de ${md.largo} x ${md.ancho} cm${v.convencion === 'eje_mayor' ? ' (eje mayor por perpendicular)' : ''}`);
  else if (num(md.largo)) out.push(`de ${md.largo} cm de largo${md.ancho === ESTADO.NO_MEDIDO ? ', ancho sin medir' : ''}`);
  else if (num(md.ancho)) out.push(`de ${md.ancho} cm de ancho${md.largo === ESTADO.NO_MEDIDO ? ', largo sin medir' : ''}`);
  if (!num(md.largo) && !num(md.ancho) && (md.largo === ESTADO.NO_MEDIDO || md.ancho === ESTADO.NO_MEDIDO)) out.push('sin medir');
  if (num(md.profundidad)) out.push(`profundidad ${md.profundidad} cm`);
  else if (md.profundidad === ESTADO.NO_VISIBLE) out.push('profundidad no valorable porque el lecho no se ve');
  else if (md.profundidad === ESTADO.NO_MEDIDO) out.push('profundidad sin medir');
  if (v?.extensionPct) out.push(`extensión ${v.extensionPct}% de superficie corporal${v.quemMetodo ? ` (${m(v.quemMetodo)})` : ''}`);
  return out;
}

function fraseTunel(tu) {
  if (!tu) return [];
  const out = [];
  if (tu.socavamiento === 'presente') {
    const tramo = tu.socavamientoDesde && tu.socavamientoHasta ? ` entre las ${tu.socavamientoDesde} y las ${tu.socavamientoHasta}` : '';
    out.push(`socavamiento${tu.socavamientoCm && !esEstado(tu.socavamientoCm) ? ` de ${tu.socavamientoCm} cm` : ''}${tramo}`);
  } else if (tu.socavamiento === ESTADO.AUSENTE) out.push('sin socavamiento');
  else if (tu.socavamiento === ESTADO.NO_EVALUADO) out.push('socavamiento no evaluado');
  if (tu.tunel === 'presente') out.push(`túnel${tu.tunelCm && !esEstado(tu.tunelCm) ? ` de ${tu.tunelCm} cm` : ''}${tu.tunelReloj ? ` hacia las ${tu.tunelReloj}` : ''}`);
  else if (tu.tunel === ESTADO.AUSENTE) out.push('sin túnel');
  else if (tu.tunel === ESTADO.NO_EVALUADO) out.push('túnel no evaluado');
  // La convención del reloj se dice una vez, solo si se usó.
  if (out.length && (tu.socavamientoDesde || tu.tunelReloj)) out[out.length - 1] += ' (reloj con la cabeza a las 12)';
  return out;
}

function fraseTejido(t) {
  if (!t) return [];
  if (t.estado === ESTADO.NO_VISIBLE) return ['lecho no visible'];
  if (t.estado === ESTADO.NO_EVALUADO) return ['lecho no evaluado'];
  const partes = TEJIDOS_LECHO.filter(({ k }) => t[k] !== undefined && Number(t[k]) > 0)
    .map(({ k, label }) => `${t[k]}% de ${m(label)}`);
  return partes.length ? [`lecho con ${lista(partes)}`] : [];
}

// La cantidad y el tipo del exudado en una frase. Exportada porque el puente
// de la evolución ancla su campo a ESTE fragmento (una sola composición).
export function fraseExudado(e) {
  if (!e) return null;
  if (e.cantidad === 'Sin exudado') return 'sin exudado';
  if (e.cantidad === ESTADO.NO_EVALUADO) return 'exudado no evaluado';
  if (e.cantidad || e.tipo) return `exudado ${[e.cantidad, e.tipo].filter(Boolean).map(m).join(' ')}`;
  return null;
}

export function fraseValoracion(l, v) {
  if (!v) return [];
  const c = camposDe(l, v);
  const out = [];
  out.push(...fraseMedidas(v));
  if (c.tunel) out.push(...fraseTunel(v.tunel));
  // Los campos propios del tipo, en el orden del catálogo.
  const simplesAntes = ['presentacionTP', 'ampolla', 'escara', 'aspecto', 'numero', 'blanquea', 'pielIntegra', 'colores', 'volumen', 'consistencia', 'pulsatil', 'temperaturaLocal', 'quemFlictenas', 'quemCircunferencial'];
  for (const campo of c.simples.filter((x) => simplesAntes.includes(x.k))) {
    if (v[campo.k] !== undefined && campo.prosa) { const f = campo.prosa(v[campo.k], l); if (f) out.push(f); }
  }
  if (c.tejido) out.push(...fraseTejido(v.tejido));
  if (c.estructuras) {
    if (v.estructuras?.visibles?.length) out.push(`con ${lista(v.estructuras.visibles.map(m))} a la vista`);
    if (v.estructuras?.palpables?.length) out.push(`con ${lista(v.estructuras.palpables.map(m))} palpable${v.estructuras.palpables.length > 1 ? 's' : ''}`);
  }
  for (const campo of c.simples.filter((x) => !simplesAntes.includes(x.k))) {
    if (v[campo.k] !== undefined && campo.prosa) { const f = campo.prosa(v[campo.k], l); if (f) out.push(f); }
  }
  if (c.itb && v.itb) out.push(v.itb === ESTADO.NO_EVALUADO ? 'ITB no evaluado' : `ITB ${v.itb}`);
  if (c.exudado && v.exudado) {
    const e = v.exudado;
    const cant = fraseExudado(e);
    if (cant) out.push(cant);
    if (e.olor === 'Mal olor tras limpiar') out.push('mal olor tras limpiar');
    else if (e.olor === 'Sin mal olor tras limpiar') out.push('sin mal olor tras limpiar');
    else if (e.olor === ESTADO.NO_EVALUADO) out.push('olor no evaluado');
  }
  if (c.bordes && v.bordes?.length) out.push(`bordes ${lista(v.bordes.map(m))}`);
  if (c.perilesional && v.perilesional?.length) {
    out.push(v.perilesional.length === 1 && v.perilesional[0] === 'Sana' ? 'piel perilesional sana' : `piel perilesional con ${lista(v.perilesional.map(m))}`);
  }
  if (c.infeccion && v.infeccion) {
    if (v.infeccion.signos?.length) out.push(`signos de infección observados: ${lista(v.infeccion.signos.map(m))}`);
    else if (v.infeccion.estado === ESTADO.AUSENTE) out.push('sin signos de infección');
    else if (v.infeccion.estado === ESTADO.NO_EVALUADO) out.push('signos de infección no evaluados');
    if (v.infeccion.interpretacion) out.push(m(v.infeccion.interpretacion));
  }
  if (v.dolor) {
    if (v.dolor.eva === ESTADO.NO_EVALUADO) out.push('dolor no evaluado');
    else if (v.dolor.eva !== undefined) out.push(`dolor EVA ${v.dolor.eva}/10${v.dolor.momento ? ` ${m(v.dolor.momento)}` : ''}`);
  }
  if (v.trayectoria) out.push(TRAYECTORIA_PROSA[v.trayectoria]);
  return out;
}

function fraseOrigen(l) {
  const p = PRESENTE_DESDE.find((x) => x.id === l.origen?.presenteDesde);
  const out = [];
  const prosa = p ? p.prosa.replace(/^detectada/, concuerda(l, 'detectada')) : null;
  // La hora va solo en la pesquisa de una LPP en el turno (excepción dictada
  // a la norma "la prosa no imprime la hora"); en otro tipo, no.
  if (p) out.push(p.id === 'detectada_turno' && l.tipo === 'LPP' && HORA_RE.test(l.hora || '') ? `${prosa} a las ${l.hora}` : prosa);
  if (l.origen?.aparicion === 'Desconocida') out.push('momento de aparición desconocido');
  else if (l.origen?.aparicion === 'Conocida' && l.origen.aparicionFecha) {
    const [y, mm, dd] = l.origen.aparicionFecha.split('-').map(Number);
    const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
    if (mm >= 1 && mm <= 12) out.push(`aparición el ${dd} de ${meses[mm - 1]} de ${y}`);
  }
  return out;
}

// El nombre y el lugar juntos. En mucosa el lugar ya dice "mucosa…" casi
// siempre: "LPP en mucosa nasal derecha", no "LPP en mucosa de mucosa nasal".
export function nombreYLugar(l) {
  const ubic = ubicacionTexto(l?.ubicacion);
  if (l?.tipo === 'LPP' && esMucosa(l)) return /^mucosa\b/.test(ubic) ? `LPP en ${ubic}` : `LPP en mucosa de ${ubic}`;
  return `${nombreLesion(l)} en ${ubic}`;
}

// Los fragmentos CON NOMBRE de la frase v2. `ancla` es la cabeza estable
// ("LPP categoría III en sacro") que usa el puente de la evolución.
// `origen: false` se usa bajo un rótulo que ya dice "Al recibir": ahí solo se
// calla el "presente al recibir" que repetiría el rótulo. Un "presente al
// ingreso hospitalario" o un momento de aparición conocido son datos propios y
// se siguen diciendo.
export function fragmentosLesionV2(l, { origen: conOrigen = true, valoracion = 'primera' } = {}) {
  const v = valoracion === 'ultima' ? ultimaValoracion(l) : (valoracion && typeof valoracion === 'object' ? valoracion : primeraValoracion(l));
  const c = camposDe(l, v);
  let cabeza = nombreYLugar(l);
  if (l.ubicacion?.bajoDispositivo) cabeza += ', bajo un dispositivo';
  if (l.tipo === 'LPP' && l.dispositivo) cabeza += `, asociada a ${m(l.dispositivo)}`;
  if (l.tipo === 'MARSI' && l.producto) cabeza += `, ${m(l.producto)}`;
  if (l.tipo === 'MARSI' && l.persiste30 && l.persiste30 !== 'No evaluado') cabeza += `, ${m(l.persiste30)}`;
  if (l.tipo === 'desgarro' && l.mecanismo && l.mecanismo !== 'Retiro de un adhesivo') cabeza += l.mecanismo === 'Desconocido' ? ', mecanismo desconocido' : `, por ${m(l.mecanismo)}`;
  if (l.tipo === 'desgarro' && l.producto) cabeza += ` (${m(l.producto)})`;
  if (l.tipo === 'operatoria' && l.estadoHerida) cabeza += `, ${l.estadoHerida === 'Afrontada' ? 'afrontada' : `con ${m(l.estadoHerida)}`}`;
  if (l.tipo === 'operatoria' && l.cierre) cabeza += `, ${l.cierre === 'Cierre por segunda intención' ? 'cierre por segunda intención' : `con ${m(l.cierre)}`}`;
  // El contexto se dice como contexto y con su fuente, jamás como causa.
  if ((l.tipo === 'equimosis' || l.tipo === 'hematoma') && l.contexto?.length) {
    const sinCausa = l.contexto.length === 1 && l.contexto[0] === 'Sin causa conocida';
    cabeza += sinCausa ? ', sin causa conocida'
      : `, en contexto de ${lista(l.contexto.filter((x) => x !== 'Sin causa conocida').map(m))}${l.fuenteContexto ? ` (${l.fuenteContexto === 'Observado' ? 'observado' : 'referido'})` : ''}`;
  }
  if (l.enCicatrizacion) cabeza += ', en cicatrización';
  const reclas = (l.historial || []).filter((h) => h.evento === 'reclasificacion');
  const ultimaRe = reclas[reclas.length - 1];
  const motivo = ultimaRe ? MOTIVO_RECLASIFICACION.find((x) => x.id === ultimaRe.motivo) : null;
  const reclasificacion = ultimaRe && motivo && ultimaRe.de
    ? `antes ${concuerda(l, 'registrada')} como ${m(CATEGORIA_LEGADO[ultimaRe.de] || ultimaRe.de)}, cambio ${motivo.prosa}`
    : null;
  const cobertura = l.cobertura ? (l.cobertura === 'Sin cobertura' ? 'sin cobertura' : `cobertura con ${m(l.cobertura)}`) : null;
  const curacion = HORA_RE.test(l.ultimaCuracionHora || '') ? `última curación a las ${l.ultimaCuracionHora}` : null;
  return {
    ancla: nombreYLugar(l),
    cabeza,
    reclasificacion,
    origen: conOrigen ? fraseOrigen(l) : fraseOrigen(l).filter((x) => x !== 'presente al recibir'),
    hallazgos: fraseValoracion(l, v),
    cobertura,
    curacion,
    medidas: fraseMedidas(v)[0] || null,
    lecho: c.tejido ? (fraseTejido(v?.tejido)[0] || null) : null,
    exudado: c.exudado ? fraseExudado(v?.exudado) : null,
  };
}

export function fraseLesionV2(l, opts = {}) {
  const f = fragmentosLesionV2(l, opts);
  return [f.cabeza, f.reclasificacion, ...f.origen, ...f.hallazgos, f.cobertura, f.curacion].filter(Boolean).join(', ');
}

// La frase de una valoración posterior (control, curación): solo lo que se
// miró en ESA valoración, encabezado por la lesión.
export function fraseControlV2(l, valoracion) {
  const enMomento = valoracion?.ts ? lesionEn(l, valoracion.ts) : l;
  const hall = fraseValoracion(enMomento, valoracion);
  if (!hall.length) return null;
  return `${cabezaCorta(enMomento)}, ${hall.join(', ')}`;
}

// "LPP categoría III en sacro": el nombre y el lugar, sin más.
export function cabezaCorta(l) {
  return nombreYLugar(l);
}

// ── La lesión en un momento de su historia ────────────────────────────────────
// La evolución es una película: lo que se escribió a las 08:00 dice la
// categoría de las 08:00. Un cambio por progresión o por nueva información es
// un hecho posterior y se narra aparte; una corrección o la definición de una
// categoría pendiente no son hechos nuevos, son el dato correcto desde el
// principio, y se quedan.
const esCambioDeHecho = (h) => h?.evento === 'reclasificacion' && h.motivo !== 'correccion';

export function lesionAlCrear(l) {
  const def = CLASIFICACION[l?.tipo];
  const hist = Array.isArray(l?.historial) ? l.historial : [];
  const creacion = hist.find((h) => h.evento === 'creacion');
  const out = { ...l, historial: hist.filter((h) => !esCambioDeHecho(h)) };
  if (def) {
    let valor = l[def.campo];
    for (let i = hist.length - 1; i >= 0; i -= 1) if (esCambioDeHecho(hist[i])) valor = hist[i].de;
    if (valor) out[def.campo] = valor; else delete out[def.campo];
  }
  // La curación se narra en su propia frase: la del registro inicial lleva la
  // cobertura con que se recibió, no la de una curación posterior.
  delete out.ultimaCuracionHora;
  if (creacion && Object.prototype.hasOwnProperty.call(creacion, 'cobertura')) {
    if (creacion.cobertura) out.cobertura = creacion.cobertura; else delete out.cobertura;
  }
  return out;
}

// La clasificación vigente en el instante `ts`: la de creación más los cambios
// de hecho ocurridos hasta ese momento.
export function lesionEn(l, ts) {
  const def = CLASIFICACION[l?.tipo];
  const base = lesionAlCrear(l);
  if (!def) return base;
  let valor = base[def.campo];
  for (const h of l.historial || []) {
    if (esCambioDeHecho(h) && (h.ts || 0) <= ts) valor = h.a;
  }
  const out = { ...base };
  if (valor) out[def.campo] = valor; else delete out[def.campo];
  return out;
}

// Lo que no se registró en una valoración, para que la ficha y el esquema lo
// digan en vez de callarlo ("No registrado: …").
export function noRegistrado(l, v = ultimaValoracion(l)) {
  const c = camposDe(l, v);
  const faltan = [];
  const md = v?.medidas || {};
  if (!md.largo && !md.ancho) faltan.push('medidas');
  if (c.profundidad && !md.profundidad) faltan.push('profundidad');
  if (c.tejido && validarTejido(v?.tejido).vacio) faltan.push('tejido del lecho');
  if (c.exudado && !v?.exudado?.cantidad) faltan.push('exudado');
  if (c.perilesional && !v?.perilesional?.length) faltan.push('piel alrededor');
  if (c.infeccion && !v?.infeccion?.signos?.length && !v?.infeccion?.estado) faltan.push('signos de infección');
  const def = CLASIFICACION[l?.tipo];
  if (def && !(l.tipo === 'LPP' && esMucosa(l)) && (!l[def.campo] || l[def.campo] === PENDIENTE)) faltan.push(def.label.toLowerCase());
  return faltan;
}

// ── Gravedad para el color del mapa ───────────────────────────────────────────
// Sin clasificación no hay severidad que afirmar: 'sin_clasificar' se pinta
// neutro. Antes una LPP sin categoría contaba como moderada.
export function nivelLesionV2(l) {
  if (l?.tipo === 'LPP') {
    if (esMucosa(l)) return 'moderado';
    const c = CATEGORIA_LEGADO[l.categoria] || l.categoria;
    if (c === 'Categoría III' || c === 'Categoría IV' || c === 'No clasificable' || c === 'Tejidos profundos') return 'severo';
    if (c === 'Categoría II') return 'moderado';
    if (c === 'Categoría I') return 'leve';
    return 'sin_clasificar';
  }
  // Una clasificación que falta o quedó pendiente no afirma gravedad. La úlcera
  // venosa es la excepción: su clase CEAP (C6) ya es la úlcera activa.
  const defC = CLASIFICACION[l?.tipo];
  if (defC && l.tipo !== 'ulcera_venosa' && (!l[defC.campo] || l[defC.campo] === PENDIENTE)) return 'sin_clasificar';
  if (l?.tipo === 'operatoria') return l.estadoHerida === 'Dehiscencia total' ? 'severo' : l.estadoHerida === 'Dehiscencia parcial' ? 'moderado' : 'leve';
  if (['ulcera_venosa', 'ulcera_arterial', 'pie_diabetico'].includes(l?.tipo)) return 'severo';
  if (l?.tipo === 'quemadura') return ['Tipo AB-B', 'Tipo B'].includes(l.categoria) ? 'severo' : l.categoria ? 'moderado' : 'sin_clasificar';
  if (l?.tipo === 'por_definir') return 'sin_clasificar';
  if (l?.tipo === 'equimosis') return 'leve';
  return 'moderado';
}

// ── Pistas: ayudan a clasificar bien o a avisar, jamás indican tratamiento ───
// Cada pista dice de dónde sale. `tono` es solo el tono elegido para el
// esquema: la persona no tiene que clasificar al paciente en un tono para ver
// la ayuda de pieles oscuras (va siempre en las LPP de piel intacta).
export function pistasLesion(l, v) {
  const out = [];
  const c = camposDe(l, v);
  const cat = CATEGORIA_LEGADO[l?.categoria] || l?.categoria;
  const tj = v?.tejido;
  if (l?.tipo === 'LPP' && !esMucosa(l)) {
    if (cat === 'Categoría I' || cat === 'Tejidos profundos') {
      out.push({ nivel: 'info', titulo: 'En pieles medias y oscuras', texto: 'El enrojecimiento o el morado pueden no verse. Compara temperatura, consistencia, edema y dolor con la piel vecina, con luz tangencial.', fuente: 'Guía internacional de LPP 2026, STA1 y CL2' });
    }
    const estructuras = Boolean(v?.estructuras?.visibles?.length || v?.estructuras?.palpables?.length);
    const tunel = v?.tunel?.tunel === 'presente' || v?.tunel?.socavamiento === 'presente';
    if (cat === 'Categoría I' && (leerNumero(v?.medidas?.profundidad) > 0 || estructuras || tunel || (tj && !esEstado(tj.estado) && TEJIDOS_LECHO.some(({ k }) => leerNumero(tj[k]) > 0)))) {
      out.push({ nivel: 'aten', titulo: 'Revisa la categoría', texto: 'La categoría I corresponde a piel intacta. Hay datos de una herida abierta en esta valoración. Revisa los hallazgos y la clasificación; puedes dejarla pendiente mientras la defines.', fuente: 'Guía internacional de LPP 2026, capítulo de clasificación' });
    }
    if (cat === 'Categoría II' && ((tj && !esEstado(tj.estado) && ['esfacelo', 'necrosis', 'granulacion'].some((k) => leerNumero(tj[k]) > 0)) || estructuras || tunel)) {
      out.push({ nivel: 'aten', titulo: 'Revisa la categoría', texto: 'Los hallazgos registrados requieren revisar la categoría II. No corresponden a ella el esfacelo, la escara, el tejido de granulación, las estructuras profundas expuestas ni los túneles. El color blanco amarillento por sí solo no identifica esfacelo. Puedes dejar la categoría pendiente mientras la defines.', fuente: 'Guía internacional de LPP 2026, capítulo de clasificación' });
    }
    if (cat === 'Categoría III' && v?.estructuras && (v.estructuras.visibles?.length || v.estructuras.palpables?.length)) {
      out.push({ nivel: 'aten', titulo: 'Revisa la categoría', texto: 'Con hueso, tendón, músculo, fascia, cartílago o ligamento visibles o palpables, la categoría es IV. Decide la estructura, no los centímetros.', fuente: 'Guía internacional de LPP 2026, capítulo de clasificación' });
    }
    if (cat === 'No clasificable' && ['talon'].includes(l?.ubicacion?.detalle)) {
      out.push({ nivel: 'info', titulo: 'Escara en el talón', texto: 'Si es estable (seca, adherida, intacta, sin eritema ni fluctuación), es la cobertura natural y no se desbrida. Tampoco en otras zonas mal perfundidas.', fuente: 'Guía internacional de LPP 2026, CL2' });
    }
    if (l?.dispositivo) out.push({ nivel: 'info', titulo: 'Por dispositivo', texto: 'Se categoriza igual que cualquier LPP; "por dispositivo" dice la causa, no la gravedad. Suele tener la forma del dispositivo.', fuente: 'Guía internacional de LPP 2026, capítulo de clasificación' });
  }
  if (l?.tipo === 'LPP' && esMucosa(l)) out.push({ nivel: 'info', titulo: 'En mucosa', texto: 'No se categoriza. Se documenta como LPP en mucosa, con el dispositivo asociado. Lo que parece esfacelo suele ser un coágulo blando, y el eritema que no blanquea no se ve en la mucosa.', fuente: 'Guía internacional de LPP 2026, CL2 y págs. 8 y 9; NT 234' });
  if (l?.tipo === 'quemadura') {
    const especial = ['cara', 'cuello', 'perine'].includes(l.ubicacion?.region) || ['palma', 'dorso_mano', 'dedos_mano', 'dorso_pie', 'dedos_pie', 'planta', 'axila', 'codo', 'hueco_popliteo', 'pliegue_inguinal', 'pliegue_inframamario'].includes(l.ubicacion?.detalle);
    if (especial) out.push({ nivel: 'aten', titulo: 'Zona especial', texto: 'Cara, cuello, manos, pies, pliegues, genitales, periné y mamas son zonas especiales de la quemadura. Avisa según el protocolo.', fuente: 'Guía Clínica GES Gran Quemado, MINSAL 2016' });
    if (['cara', 'cuello'].includes(l.ubicacion?.region)) out.push({ nivel: 'crit', titulo: 'Vía aérea', texto: 'Con quemadura de cara o cuello, busca signos de injuria inhalatoria (vibrisas chamuscadas, hollín en la boca, cambio de voz, estridor, dificultad respiratoria) y avisa de inmediato si aparecen.', fuente: 'Guía Clínica GES Gran Quemado, MINSAL 2016; ISBI 2016' });
    if (v?.quemCircunferencial === 'Circunferencial') out.push({ nivel: 'crit', titulo: 'Quemadura en manguito', texto: 'Una quemadura que rodea el segmento puede comprimir. Vigila la perfusión distal y avisa sin esperar a que falten los pulsos, porque su pérdida es un hallazgo tardío.', fuente: 'Guía Clínica GES Gran Quemado, MINSAL 2016; ISBI 2016' });
    if (v?.quemFlictenas === 'Con flictenas') out.push({ nivel: 'info', titulo: 'Flictenas', texto: 'Una flictena puede ocultar una quemadura más profunda. La profundidad cambia en los primeros días, así que vuelve a evaluarla.', fuente: 'ISBI 2016, sección 7; MINSAL 2016' });
  }
  if (l?.tipo === 'MARSI' && (l.ubicacion?.detalle === 'periestomal' || l.producto === 'Bolsa de ostomía')) out.push({ nivel: 'info', titulo: 'Alrededor de una ostomía', texto: 'El criterio de 30 minutos no se usa aquí, porque la bolsa no puede quedar fuera tanto tiempo. La lesión por humedad del efluente y la del adhesivo se ven distinto. La humedad deja maceración y eritema, y el adhesivo, piel arrancada, ampollas o erosión al retirar la bolsa.', fuente: 'LeBlanc y cols. 2019; Wounds UK 2025, pág. 18' });
  if (l?.tipo === 'DAI') out.push({ nivel: 'info', titulo: 'DAI o LPP', texto: 'No se distinguen solo por la ubicación. Mira la exposición a orina o deposiciones, la distribución (difusa, en espejo, bordes mal definidos) y si hay presión o cizalla. Pueden coexistir, y cada una se registra por separado.', fuente: 'IAD Best Practice Update 2026' });
  if (l?.tipo === 'desgarro') out.push({ nivel: 'info', titulo: 'Para no confundir', texto: 'Un desgarro no atraviesa el tejido subcutáneo; si lo hace, es una laceración. Mide solo el lecho abierto que se ve, no el colgajo.', fuente: 'ISTAP 2025' });
  if (l?.tipo === 'MARSI' && l?.subtipo === 'Dermatitis de contacto') out.push({ nivel: 'info', titulo: 'Irritativa o alérgica', texto: 'Distinguirlas puede ser difícil. Que la lesión calce con el adhesivo o se salga de él orienta, pero no basta para el diagnóstico. Si no está establecido, déjalo sin determinar.', fuente: 'McNichol y cols. 2013; OT MINSAL 2025' });
  if (l?.tipo === 'pie_diabetico') out.push({ nivel: 'info', titulo: 'Cómo se describe', texto: 'Registra cada variable por separado. Wagner ya no se recomienda porque no mide isquemia ni infección.', fuente: 'IWGDF 2023, clasificación' });
  if (l?.tipo === 'ulcera_venosa' && (!v?.itb || v.itb === ESTADO.NO_EVALUADO)) out.push({ nivel: 'info', titulo: 'Antes de llamarla venosa', texto: 'La guía europea pide evaluar de forma objetiva la circulación arterial de esa pierna (ITB).', fuente: 'ESVS 2022, recomendación 68' });
  const itb = v?.itb && !esEstado(v.itb) ? Number(String(v.itb).replace(',', '.')) : null;
  if (itb !== null && itb > 1.4) out.push({ nivel: 'info', titulo: 'ITB mayor de 1,4', texto: 'Se informa como no compresible, porque las arterias rígidas dan un valor falsamente alto.', fuente: 'Global Vascular Guidelines 2019' });
  if (itb !== null && itb >= 0.6 && itb < 0.8 && l?.tipo === 'ulcera_venosa') out.push({ nivel: 'aten', titulo: 'ITB entre 0,6 y 0,8', texto: 'La guía europea lo trata como enfermedad arterial asociada. Avisa según el protocolo antes de cualquier compresión.', fuente: 'ESVS 2022, sección 6.3.3' });
  if (itb !== null && itb < 0.6 && l?.tipo === 'ulcera_venosa') out.push({ nivel: 'crit', titulo: 'ITB menor de 0,6', texto: 'Es enfermedad arterial importante en esa pierna. Avisa según el protocolo del servicio antes de cualquier compresión.', fuente: 'ESVS 2022, recomendación 72' });
  if (l?.tipo === 'ulcera_arterial' && (v?.arterialDolorReposo === 'Con dolor en reposo' || (v?.gangrena && v.gangrena !== 'Sin gangrena'))) out.push({ nivel: 'crit', titulo: 'Posible isquemia que amenaza la extremidad', texto: 'Dolor en reposo o gangrena con enfermedad arterial es una señal de alarma. Avisa de inmediato según el protocolo del servicio.', fuente: 'Global Vascular Guidelines 2019' });
  const sangre = l?.tipo === 'equimosis' || l?.tipo === 'hematoma';
  const region = l?.ubicacion?.region;
  const extremidad = ['miembro_superior', 'miembro_inferior'].includes(region);
  if (sangre) {
    if (l.tipo === 'equimosis' && v?.blanquea === 'Blanquea a la presión') out.push({ nivel: 'aten', titulo: 'Si blanquea, no es equimosis', texto: 'La sangre fuera de los vasos no se pone pálida al presionarla. Algo que blanquea es eritema, así que revisa qué tipo de lesión registras.', fuente: 'MedlinePlus, Sangrado dentro de la piel; DermNet, Terminology' });
    if (l.tipo === 'hematoma' && (v?.cambio === 'Más grande' || (v?.volumen === 'Con aumento de volumen' && v?.consistencia === 'Tensa'))) out.push({ nivel: 'crit', titulo: 'Hematoma que crece o está tenso', texto: 'Avisa según el protocolo del servicio y vigila el estado distal.', fuente: 'StatPearls: Cardiac Catheterization Risks and Complications; Pseudoaneurysm' });
    if (v?.pulsatil === 'Late a la palpación') out.push({ nivel: 'crit', titulo: 'Late', texto: 'Una masa que late en un sitio de punción o acceso arterial no es un hematoma simple. Avisa de inmediato según el protocolo.', fuente: 'StatPearls: Pseudoaneurysm; Femoral Artery Pseudoaneurysm' });
    if (v?.pielIntegra === 'Con sangrado activo') out.push({ nivel: 'crit', titulo: 'Sangrado activo', texto: 'Avisa según el protocolo del servicio.', fuente: 'StatPearls: Cardiac Catheterization Risks and Complications' });
    if (extremidad && (v?.dolorPalpacion === 'Dolor que no calza con lo que se ve' || v?.consistencia === 'Tensa')) out.push({ nivel: 'crit', titulo: 'Dolor desproporcionado o compartimento tenso', texto: 'En una extremidad puede ser un síndrome compartimental, y un pulso presente no lo descarta. Avisa de inmediato según el protocolo.', fuente: 'StatPearls: Acute Compartment Syndrome' });
    if (l.tipo === 'hematoma' && region === 'cuello') out.push({ nivel: 'aten', titulo: 'Hematoma en el cuello', texto: 'Después de una cirugía de cuello puede comprometer la vía aérea. Vigila dificultad para tragar, aumento de volumen, ansiedad, respiración rápida o estridor, que puede ser tardío. Un drenaje funcionando no lo descarta.', fuente: 'Iliff y cols. 2022, guía DAS, BAETS y ENT UK (DESATS)' });
    if (l.tipo === 'hematoma' && region === 'abdomen' && v?.anticoagulacion === 'Sí') out.push({ nivel: 'aten', titulo: 'Masa en la pared abdominal con anticoagulante', texto: 'Puede ser un hematoma de la vaina de los rectos, sobre todo en zonas de inyección subcutánea. Vigila pulso y presión, y avisa según el protocolo.', fuente: 'StatPearls: Rectus Sheath Hematoma' });
    if (v?.anticoagulacion === 'Sí' && (v?.cambio === 'Más grande' || v?.numero === 'Varias')) out.push({ nivel: 'aten', titulo: 'Con anticoagulante', texto: 'Equimosis nuevas o múltiples y hematomas que crecen en una persona anticoagulada se avisan según el protocolo, sobre todo con palidez, taquicardia o hipotensión.', fuente: 'StatPearls: Anticoagulation Safety' });
    if (v?.numero === 'Varias' && l.contexto?.length === 1 && l.contexto[0] === 'Sin causa conocida') out.push({ nivel: 'aten', titulo: 'Varias y sin causa conocida', texto: 'Descríbelas sin asignarles causa y avisa según el protocolo del servicio, incluido el de protección si corresponde. Piel fina, anticoagulantes y reposo también producen moretones.', fuente: 'MedlinePlus; StatPearls: Elder Abuse' });
    // Una mancha violácea sobre un apoyo o bajo un dispositivo, sin golpe ni
    // punción que la explique, se valora como posible LPP de tejidos profundos.
    const sobreApoyo = zonaDeUbicacion(l.ubicacion) !== 'otra' || l.ubicacion?.bajoDispositivo;
    const sinContexto = !l.contexto?.length || (l.contexto.length === 1 && l.contexto[0] === 'Sin causa conocida');
    if (sobreApoyo && sinContexto) out.push({ nivel: 'info', titulo: '¿Podría ser una LPP?', texto: 'Sobre una zona de apoyo o bajo un dispositivo, sin golpe ni punción que la explique, valórala como posible LPP en tejidos profundos. No toda mancha morada es presión, y no toda es un moretón.', fuente: 'NPIAP, póster DTPI or an Imposter? (2021); Guía internacional de LPP 2026, clasificación' });
  }
  if (l?.tipo === 'hematoma' && v?.neurovascular === 'Con alteración de pulso, color, temperatura o sensibilidad') out.push({ nivel: 'crit', titulo: 'Alteración distal', texto: 'Avisa de inmediato según el protocolo del servicio.', fuente: 'StatPearls: Femoral Artery Pseudoaneurysm; Pseudoaneurysm' });
  if (c.infeccion && v?.infeccion?.signos?.length) {
    const generales = v.infeccion.signos.filter((s) => SIGNOS_GENERALES.includes(s));
    if (generales.length) out.push({ nivel: 'crit', titulo: 'Manifestaciones generales', texto: 'Con signos generales junto a la herida, avisa según el protocolo del servicio. Los signos vitales se registran en su sistema; la sospecha de sepsis la evalúa el equipo con sus criterios.', fuente: 'IWII 2026, continuo de infección' });
    else if (v.infeccion.signos.length >= 2) out.push({ nivel: 'aten', titulo: 'Varios signos de infección', texto: 'El IWII pide sospechar infección ante varios signos, no por uno solo. Si tu interpretación es de sospecha, regístrala y avisa según el protocolo.', fuente: 'IWII 2026, pág. 25' });
  }
  return out;
}
